import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PRESET_STYLE_OPTIONS } from '../presets/defaultPresets';
import {
  DEFAULT_SNAP_GRID_TYPE,
  SNAP_THRESHOLD,
  SNAP_GRID_TYPE_CENTER_CROSS,
  SNAP_GRID_TYPE_LOWER_THIRD,
  SNAP_GRID_TYPE_THIRDS,
  buildCanvasVisibleGridLines,
  snapTranslatedBounds,
} from './editorSnapUtils';
import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  TEMPLATE_KEY,
  applySelectionAffineTransform,
  boundsCenter,
  clamp,
  getAllSelectionKeys,
  getBoundsForSelectionKey,
  getSelectionFrameBoundsWorld,
  getSelectionKeysIntersectingBounds,
  getSelectionBoundsWorld,
  normalizeAngle,
  normalizeMarqueeBounds,
  normalizeDragRotationDelta,
  pointInBounds,
  primitiveSelectionKey,
  resolveSelectionFromHit,
  resetSelectionTransforms,
  sanitizeSelectionKeys,
  selectionKeyToPrimitiveId,
} from './presetOverlayTransformUtils';
import {
  applyBoundShapeHandleResize,
  resolveBoundResizeGesture,
} from './presetOverlayBoundResizeUtils';
import {
  TEMPLATE_MAIN_TEXT_LAYER_ID,
  TEMPLATE_SHAPE_LAYER_ID,
  TEMPLATE_SUB_TEXT_LAYER_ID,
  getPresetLayerPanelOrder,
  layerIdToPrimitiveId,
  normalizePresetLayerLocks,
  normalizePresetLayerOrder,
  primitiveLayerId,
  reorderLayerOrderByPanelIndices,
} from '../presets/presetLayerUtils';
import {
  bindingReferencesSourceTextId,
  adjustAutoSizeBindingForTextMove,
  captureAutoSizeBinding,
  getAutoSizeBindingSourceIds,
  isAutoSizeBindingSource,
  isAutoSizeBindingTarget,
  recalibrateAutoSizeBinding,
  resolveBindingSourceBounds,
  sanitizeAutoSizeBinding,
} from '../presets/primitiveAutoSizeBindingUtils';
import ImageImportOverlay from './ImageImportOverlay';
import {
  buildImageReplacementPatch,
  getImagePrimitiveEntries,
  isImagePrimitive,
} from '../presets/imageLayerUtils';
import {
  getTextLayerFontSearchResults,
  TEXT_LAYER_FONT_PICKER_DEFAULT_FAMILIES,
  TEXT_LAYER_FONT_SEARCH_RESULT_LIMIT,
  sanitizeTextLayerFontFamily,
} from '../fonts/textLayerFonts';
import { ensureGoogleFontsLoaded } from '../fonts/googleFontLoader';
import { stampPrimitiveResetSnapshot } from '../presets/primitiveResetSnapshot';

const MARQUEE_DRAG_THRESHOLD = 4;
const PRESET_ELEMENT_SNAP_PROXIMITY = 120;
const MOVE_AXIS_LOCK_THRESHOLD = 4;
const PRESET_EDITOR_SNAP_STORAGE_KEY = 'lowerThirds.ui.presetEditorSnap.enabled';
const PRESET_EDITOR_SNAP_GRID_TYPE_STORAGE_KEY = 'lowerThirds.ui.presetEditorSnap.gridType';
const PRESET_EDITOR_SNAP_GRID_VISIBLE_STORAGE_KEY = 'lowerThirds.ui.presetEditorSnap.gridVisible';
const PRESET_EDITOR_SNAP_MODE_STORAGE_KEY = 'lowerThirds.ui.presetEditorSnap.mode';
const PRESET_SNAP_MODE_GRID_ELEMENTS = 'grid-elements';
const PRESET_SNAP_MODE_ELEMENTS_ONLY = 'elements-only';
const PRESET_SNAP_MODE_GRID_ONLY = 'grid-only';
const SNAP_GRID_TYPE_OPTIONS = [
  { value: SNAP_GRID_TYPE_CENTER_CROSS, label: 'Center' },
  { value: SNAP_GRID_TYPE_THIRDS, label: 'Thirds' },
  { value: SNAP_GRID_TYPE_LOWER_THIRD, label: 'Lower third' },
];
const PRESET_SNAP_MODE_OPTIONS = [
  { value: PRESET_SNAP_MODE_GRID_ELEMENTS, label: 'Grid + elements' },
  { value: PRESET_SNAP_MODE_ELEMENTS_ONLY, label: 'Elements only' },
  { value: PRESET_SNAP_MODE_GRID_ONLY, label: 'Grid only' },
];
const PRESET_PREVIEW_ZOOM_MIN = 50;
const PRESET_PREVIEW_ZOOM_MAX = 200;
const PRESET_PREVIEW_ZOOM_STEP = 25;
const PRESET_PREVIEW_ZOOM_DEFAULT = 100;
const PRESET_PREVIEW_BASE_FIT_PERCENT = 98;
const TEXT_FONT_DEFAULT_OPTION_LABEL = 'Default (Arial/system)';
const TEXT_FONT_SEARCH_PREVIEW_LIMIT = 6;
const DYNAMIC_TEXT_TOOLTIP = 'Dynamic texts can be changed for each person through the people panel';

const InfoCircleIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
    <path d="M12 17V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="12" cy="8" r="1" fill="currentColor" />
  </svg>
);

const MinusIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3 8H13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const PlusIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M8 3V13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M3 8H13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const readStoredBoolean = (key, fallback) => {
  if (typeof window === 'undefined' || !window.localStorage) return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return fallback;
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return fallback;
  } catch {
    return fallback;
  }
};

const readStoredString = (key, fallback, allowedValues = []) => {
  if (typeof window === 'undefined' || !window.localStorage) return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return fallback;
    if (allowedValues.length && !allowedValues.includes(raw)) return fallback;
    return raw;
  } catch {
    return fallback;
  }
};
const isEditableKeyTarget = (target) => {
  if (!(target instanceof Element)) return false;
  const tagName = target.tagName?.toLowerCase();
  if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') return true;
  if (target.isContentEditable) return true;
  return Boolean(target.closest('[contenteditable="true"]'));
};

const offsetBoundsByDelta = (bounds, delta) => {
  if (!bounds) return null;
  const dx = Number(delta?.x) || 0;
  const dy = Number(delta?.y) || 0;
  return {
    minX: bounds.minX + dx,
    minY: bounds.minY + dy,
    maxX: bounds.maxX + dx,
    maxY: bounds.maxY + dy,
    width: bounds.width,
    height: bounds.height,
  };
};

const choosePreferredAxisSnap = (gridSnap, elementSnap) => {
  if (!gridSnap) return elementSnap || null;
  if (!elementSnap) return gridSnap;
  return elementSnap.distance <= gridSnap.distance ? elementSnap : gridSnap;
};

const intervalGap = (aMin, aMax, bMin, bMax) => {
  if (aMax < bMin) return bMin - aMax;
  if (bMax < aMin) return aMin - bMax;
  return 0;
};

const buildWorldBoundsFromLocalBounds = (bounds, offsetX, offsetY) => {
  if (!bounds) return null;
  const dx = Number(offsetX) || 0;
  const dy = Number(offsetY) || 0;
  return {
    minX: bounds.minX + dx,
    minY: bounds.minY + dy,
    maxX: bounds.maxX + dx,
    maxY: bounds.maxY + dy,
    width: bounds.width,
    height: bounds.height,
  };
};

const getBoundsAxisAnchors = (bounds, axis) => {
  if (!bounds) return [];
  if (axis === 'x') {
    return [
      { key: 'minX', value: bounds.minX, kind: 'edge' },
      { key: 'centerX', value: bounds.minX + (bounds.width / 2), kind: 'center' },
      { key: 'maxX', value: bounds.maxX, kind: 'edge' },
    ];
  }
  return [
    { key: 'minY', value: bounds.minY, kind: 'edge' },
    { key: 'centerY', value: bounds.minY + (bounds.height / 2), kind: 'center' },
    { key: 'maxY', value: bounds.maxY, kind: 'edge' },
  ];
};

const choosePreferredElementAxisSnap = (current, candidate) => {
  if (!candidate) return current || null;
  if (!current) return candidate;
  if (candidate.distance < current.distance) return candidate;
  if (candidate.distance > current.distance) return current;

  // Prefer center snaps over edge snaps when distances tie for stability.
  const currentRank = current.anchorKind === 'center' ? 0 : 1;
  const candidateRank = candidate.anchorKind === 'center' ? 0 : 1;
  if (candidateRank < currentRank) return candidate;
  if (candidateRank > currentRank) return current;
  return current;
};

const PRIMITIVE_COLOR_OPTIONS = [
  { value: 'primary', label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'textUpper', label: 'Text Upper' },
  { value: 'textLower', label: 'Text Lower' },
];
const PRIMITIVE_COLOR_MODES = [
  { value: 'theme', label: 'Preset color' },
  { value: 'custom', label: 'Custom color' },
];
const primitiveColorRoleSet = new Set(PRIMITIVE_COLOR_OPTIONS.map((option) => option.value));
const primitiveColorModeSet = new Set(PRIMITIVE_COLOR_MODES.map((option) => option.value));
const textSourceSet = new Set(['main', 'sub', 'generic']);
const hexColorPattern = /^#([0-9a-f]{6}|[0-9a-f]{3})$/i;

const primitiveDraftId = (shape, index) => shape?.id || `shape-${index}`;
const cloneDraftPreset = (preset) => JSON.parse(JSON.stringify(preset));
const withPrimitiveResetBaseline = (shape, options) => stampPrimitiveResetSnapshot(shape, options);
const normalizePrimitiveColorRole = (value, fallback = 'primary') => (
  primitiveColorRoleSet.has(value) ? value : fallback
);
const normalizePrimitiveColorMode = (value, fallback = 'theme') => (
  primitiveColorModeSet.has(value) ? value : fallback
);
const normalizePrimitiveColorValue = (value, fallback = '#ffffff') => (
  typeof value === 'string' && hexColorPattern.test(value) ? value : fallback
);
const normalizeTextSource = (value, fallback = 'main') => (
  textSourceSet.has(value) ? value : fallback
);
const isSubtitleStyleTextSource = (value) => {
  const textSource = normalizeTextSource(value, 'main');
  return textSource === 'sub' || textSource === 'generic';
};
const mergeUniqueFamilies = (...groups) => {
  const unique = new Set();
  groups.forEach((group) => {
    if (!Array.isArray(group)) return;
    group.forEach((value) => {
      if (typeof value !== 'string' || !value.trim()) return;
      unique.add(value);
    });
  });
  return [...unique];
};
const buildFontPreviewFamily = (family) => {
  if (typeof family !== 'string' || !family.trim()) return 'Arial, sans-serif';
  const escapedFamily = family
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"');
  return `"${escapedFamily}", Arial, sans-serif`;
};

const LayerLockOpenIcon = () => (
  <svg className="layer-action-lock-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 19V11C6 10.4477 6.44772 10 7 10H17C17.5523 10 18 10.4477 18 11V19C18 19.5523 17.5523 20 17 20H7C6.44772 20 6 19.5523 6 19Z" stroke="currentColor" strokeLinejoin="round" />
    <circle cx="12" cy="15" r="2" stroke="currentColor" strokeLinejoin="round" />
    <path d="M16 10V6.5C16 5.11929 17.1193 4 18.5 4V4V4C19.8807 4 21 5.11929 21 6.5V10" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LayerLockClosedIcon = () => (
  <svg className="layer-action-lock-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 19V11C6 10.4477 6.44772 10 7 10H17C17.5523 10 18 10.4477 18 11V19C18 19.5523 17.5523 20 17 20H7C6.44772 20 6 19.5523 6 19Z" stroke="currentColor" strokeLinejoin="round" />
    <circle cx="12" cy="15" r="2" stroke="currentColor" strokeLinejoin="round" />
    <path d="M8 10V8C8 5.79086 9.79086 4 12 4C14.2091 4 16 5.79086 16 8V10H8Z" stroke="currentColor" strokeLinejoin="round" />
  </svg>
);

const LayerEyeOpenIcon = () => (
  <svg className="layer-action-eye-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M15.0007 12C15.0007 13.6569 13.6576 15 12.0007 15C10.3439 15 9.00073 13.6569 9.00073 12C9.00073 10.3431 10.3439 9 12.0007 9C13.6576 9 15.0007 10.3431 15.0007 12Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12.0012 5C7.52354 5 3.73326 7.94288 2.45898 12C3.73324 16.0571 7.52354 19 12.0012 19C16.4788 19 20.2691 16.0571 21.5434 12C20.2691 7.94291 16.4788 5 12.0012 5Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LayerEyeClosedIcon = () => (
  <svg className="layer-action-eye-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M2.99902 3L20.999 21M9.8433 9.91364C9.32066 10.4536 8.99902 11.1892 8.99902 12C8.99902 13.6569 10.3422 15 11.999 15C12.8215 15 13.5667 14.669 14.1086 14.133M6.49902 6.64715C4.59972 7.90034 3.15305 9.78394 2.45703 12C3.73128 16.0571 7.52159 19 11.9992 19C13.9881 19 15.8414 18.4194 17.3988 17.4184M10.999 5.04939C11.328 5.01673 11.6617 5 11.9992 5C16.4769 5 20.2672 7.94291 21.5414 12C21.2607 12.894 20.8577 13.7338 20.3522 14.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LayerDuplicateIcon = () => (
  <svg className="layer-action-dup-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="4" y="8" width="12" height="12" rx="1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 6V5C8 4.44772 8.44772 4 9 4H19C19.5523 4 20 4.44772 20 5V15C20 15.5523 19.5523 16 19 16H18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="2 2" />
  </svg>
);

const LayerDeleteIcon = () => (
  <svg className="layer-action-delete-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M10 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 7H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6 7H12H18V18C18 19.6569 16.6569 21 15 21H9C7.34315 21 6 19.6569 6 18V7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7H9V5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AddImageLayerIcon = () => (
  <svg className="primitive-image-add-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3.5" y="4.5" width="17" height="15" rx="2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6.5 15.5L10 12C10.2761 11.7239 10.7239 11.7239 11 12L14.5 15.5M13 14L14.75 12.25C15.0261 11.9739 15.4739 11.9739 15.75 12.25L17.5 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="14.5" cy="9" r="1.1" fill="currentColor" stroke="none" />
    <g className="primitive-image-add-icon-plus" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="18" r="3.5" fill="none" />
      <path d="M18 16.4V19.6M16.4 18H19.6" />
    </g>
  </svg>
);

const resolvePrimitiveColorDraft = (shape) => {
  const legacyRole = normalizePrimitiveColorRole(shape?.colorRole, 'primary');
  return {
    legacyRole,
    fillMode: normalizePrimitiveColorMode(shape?.fillColorMode, 'theme'),
    fillRole: normalizePrimitiveColorRole(shape?.fillColorRole, legacyRole),
    fillValue: normalizePrimitiveColorValue(shape?.fillColorValue, '#ffffff'),
    strokeMode: normalizePrimitiveColorMode(shape?.strokeColorMode, 'theme'),
    strokeRole: normalizePrimitiveColorRole(shape?.strokeColorRole, legacyRole),
    strokeValue: normalizePrimitiveColorValue(shape?.strokeColorValue, '#ffffff'),
  };
};

const createPrimitiveDraft = (type) => {
  const base = {
    id: `shape-${Date.now()}-${Math.floor(Math.random() * 1000000)}`,
    type,
    x: 60,
    y: 40,
    width: 220,
    height: 80,
    rotation: 0,
    strokeWidth: 3,
    colorRole: 'primary',
    fillColorMode: 'theme',
    fillColorRole: 'primary',
    fillColorValue: '#ffffff',
    strokeColorMode: 'theme',
    strokeColorRole: 'primary',
    strokeColorValue: '#ffffff',
    fill: true,
    stroke: false,
    mainTextVisible: false,
    subTextVisible: false,
    cornerRadius: 16,
    skewX: 0,
    layerKind: 'shape',
    textSource: 'main',
    textPaddingX: 0,
    textFontRatio: 0.8,
    textBaselineRatio: 0.78,
    templateFamily: null,
    templatePart: null,
    autoSizeBinding: null,
  };
  if (type === 'line') return withPrimitiveResetBaseline({ ...base, height: 4, fill: false, stroke: true, cornerRadius: 0 });
  if (type === 'circle') return withPrimitiveResetBaseline({ ...base, width: 90, height: 90, cornerRadius: 0 });
  if (type === 'parallelogram') return withPrimitiveResetBaseline({ ...base, cornerRadius: 0, skewX: -16 });
  if (type === 'rect') return withPrimitiveResetBaseline({ ...base, cornerRadius: 0 });
  return withPrimitiveResetBaseline(base);
};

const createTextPrimitiveDraft = (textSource = 'main', baseOverride = {}) => {
  const normalizedTextSource = normalizeTextSource(textSource, 'main');
  const isMain = normalizedTextSource === 'main';
  const isSubtitleStyle = !isMain;
  return withPrimitiveResetBaseline({
    ...createPrimitiveDraft('rect'),
    x: 80,
    y: isSubtitleStyle ? 92 : 42,
    width: isSubtitleStyle ? 220 : 260,
    height: isSubtitleStyle ? 34 : 44,
    colorRole: isMain ? 'textUpper' : 'textLower',
    fillColorRole: isMain ? 'textUpper' : 'textLower',
    strokeColorRole: isMain ? 'textUpper' : 'textLower',
    textPaddingX: 0,
    textFontRatio: isMain ? 0.82 : 0.76,
    textBaselineRatio: isMain ? 0.8 : 0.78,
    textFontFamily: null,
    textValue: normalizedTextSource === 'generic' ? 'Generic text' : '',
    hideFromPeoplePanel: false,
    skewX: 0,
    ...baseOverride,
    id: `shape-${Date.now()}-${Math.floor(Math.random() * 1000000)}`,
    type: baseOverride.type || 'rect',
    layerKind: 'text',
    textSource: normalizedTextSource,
    fill: false,
    stroke: false,
    cornerRadius: 0,
    templateFamily: null,
    templatePart: null,
  });
};

const createImagePrimitiveDraft = (
  imageData,
  cropMode,
  naturalWidth,
  naturalHeight,
  placementBounds = null,
  canvasWidth = CANVAS_WIDTH,
  canvasHeight = CANVAS_HEIGHT,
) => {
  const baseSize = 140;
  const isOriginal = cropMode === 'original' && naturalWidth && naturalHeight;
  let drawWidth = baseSize;
  let drawHeight = baseSize;

  if (isOriginal) {
    const scale = Math.min(baseSize / naturalWidth, baseSize / naturalHeight);
    drawWidth = Math.round(naturalWidth * scale);
    drawHeight = Math.round(naturalHeight * scale);
  }

  const centerX = placementBounds && Number.isFinite(placementBounds.minX) && Number.isFinite(placementBounds.width)
    ? placementBounds.minX + (placementBounds.width / 2)
    : (canvasWidth / 2);
  const centerY = placementBounds && Number.isFinite(placementBounds.minY) && Number.isFinite(placementBounds.height)
    ? placementBounds.minY + (placementBounds.height / 2)
    : (canvasHeight / 2);
  const x = Math.round(centerX - (drawWidth / 2));
  const y = Math.round(centerY - (drawHeight / 2));

  return withPrimitiveResetBaseline({
    ...createPrimitiveDraft('image'),
    width: drawWidth,
    height: drawHeight,
    x,
    y,
    layerKind: 'image',
    imageData,
    imageCropMode: cropMode || 'square',
    fill: false,
    stroke: false,
    cornerRadius: cropMode === 'circle' ? 999 : 0,
  }, { overwrite: true });
};

const PrimitiveShapeIcon = ({ type, className = '' }) => {
  const classes = ['primitive-shape-icon', className].filter(Boolean).join(' ');
  if (type === 'line') {
    return (
      <svg className={classes} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <line x1="4" y1="12" x2="20" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }
  if (type === 'circle') {
    return (
      <svg className={classes} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="2" />
      </svg>
    );
  }
  if (type === 'roundedRect') {
    return (
      <svg className={classes} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="4.5" y="5.5" width="15" height="13" rx="4" stroke="currentColor" strokeWidth="2" />
      </svg>
    );
  }
  if (type === 'parallelogram') {
    return (
      <svg className={classes} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M7 5.5H19.5L17 18.5H4.5L7 5.5Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg className={classes} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4.5" y="5.5" width="15" height="13" rx="0" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
};

const PRIMITIVE_ADD_OPTIONS = [
  { value: 'line', label: 'Line', ariaLabel: 'line' },
  { value: 'rect', label: 'Rectangle', ariaLabel: 'rectangle' },
  { value: 'circle', label: 'Circle', ariaLabel: 'circle' },
  { value: 'roundedRect', label: 'Rounded rectangle', ariaLabel: 'rounded rectangle' },
];

const TEXT_ADD_OPTIONS = [
  { value: 'main', label: 'Title', ariaLabel: 'title text' },
  { value: 'sub', label: 'Sub-title', ariaLabel: 'sub-title text' },
  { value: 'generic', label: 'Generic text', ariaLabel: 'generic text' },
];
const TEMPLATE_BASE_LAYER_IDS = new Set([
  TEMPLATE_SHAPE_LAYER_ID,
  TEMPLATE_MAIN_TEXT_LAYER_ID,
  TEMPLATE_SUB_TEXT_LAYER_ID,
]);

const toRadians = (degrees) => (degrees * Math.PI) / 180;
const projectVectorToAxes = (vector, axesRotateDeg = 0) => {
  const radians = toRadians(axesRotateDeg);
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return {
    x: vector.x * cos + vector.y * sin,
    y: -vector.x * sin + vector.y * cos,
  };
};

const isTextPrimitive = (shape) => shape?.layerKind === 'text';
const isTemplateManagedPrimitive = (shape) => (
  Boolean(shape?.templatePart)
  || (typeof shape?.templateFamily === 'string' && shape.templateFamily.trim())
);

const normalizeLayerName = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

const getPrimitiveLayerFallbackLabel = (primitive, index, genericLabelById = null) => {
  if (!primitive) return `Shape ${index + 1}`;
  if (isTextPrimitive(primitive)) {
    const textSource = normalizeTextSource(primitive.textSource, 'main');
    if (textSource === 'sub') return 'Sub-title text';
    if (textSource === 'generic') return genericLabelById?.get(primitive.id) || 'Generic text';
    return 'Title text';
  }
  if (primitive.templatePart) {
    const labelMap = {
      body: 'Template body',
      accent: 'Template accent',
      'body-bar': 'Template bar',
      'body-tip': 'Diagonal tip',
      'body-main': 'Template name box',
      'body-sub': 'Template title box',
      'connector-line': 'Edge connector',
      'connector-cap': 'Edge cap',
      'logo-ring': 'Logo ring',
      'logo-plate': 'Logo plate',
      'logo-icon': 'Template logo',
    };
    if (labelMap[primitive.templatePart]) return labelMap[primitive.templatePart];
  }
  return `Shape ${index + 1}: ${primitive.type}`;
};

const getPrimitiveLayerLabel = (primitive, index, genericLabelById = null) => (
  normalizeLayerName(primitive?.layerName) || getPrimitiveLayerFallbackLabel(primitive, index, genericLabelById)
);

const findPrimitiveById = (shapePrimitives, primitiveId) => (
  (Array.isArray(shapePrimitives) ? shapePrimitives : []).find((shape, index) => (
    primitiveDraftId(shape, index) === primitiveId
  )) || null
);

const findGeometryPrimitiveById = (geometry, primitiveId) => (
  (Array.isArray(geometry?.primitives) ? geometry.primitives : []).find((shape) => shape?.id === primitiveId) || null
);

const patchShapePrimitiveById = (shapePrimitives, primitiveId, updater) => {
  let changed = false;
  const next = (Array.isArray(shapePrimitives) ? shapePrimitives : []).map((shape, index) => {
    if (primitiveDraftId(shape, index) !== primitiveId) return shape;
    const updated = typeof updater === 'function' ? updater(shape) : { ...shape, ...updater };
    changed = true;
    return updated;
  });
  return changed ? next : null;
};

const buildTextBoundsByIdFromGeometry = (geometry) => new Map(
  (Array.isArray(geometry?.primitives) ? geometry.primitives : [])
    .filter((shape) => shape?.id && shape?.contentLocalBounds)
    .map((shape) => [shape.id, shape.contentLocalBounds]),
);

const removeSourceTextIdFromBinding = (binding, shape, sourceTextId) => {
  const sanitized = sanitizeAutoSizeBinding(binding, shape);
  if (!sanitized || !bindingReferencesSourceTextId(sanitized, sourceTextId)) return sanitized;
  const nextSourceTextIds = getAutoSizeBindingSourceIds(sanitized)
    .filter((id) => id !== sourceTextId);
  if (!nextSourceTextIds.length) return null;
  return sanitizeAutoSizeBinding({
    ...sanitized,
    sourceTextId: nextSourceTextIds[0],
    sourceTextIds: nextSourceTextIds,
  }, shape);
};

const clearBindingsReferencingTextId = (shapePrimitives, sourceTextId) => {
  if (!sourceTextId) return shapePrimitives;
  let changed = false;
  const next = (Array.isArray(shapePrimitives) ? shapePrimitives : []).map((shape) => {
    const binding = sanitizeAutoSizeBinding(shape?.autoSizeBinding, shape);
    if (!binding || !bindingReferencesSourceTextId(binding, sourceTextId)) return shape;
    const nextBinding = removeSourceTextIdFromBinding(binding, shape, sourceTextId);
    changed = true;
    return { ...shape, autoSizeBinding: nextBinding };
  });
  return changed ? next : shapePrimitives;
};

const PresetOverlay = ({
  isOpen,
  presets,
  selectedPresetId,
  draftPreset,
  isDraftDirty,
  onClose,
  onSelectPreset,
  onDraftChange,
  canUndoDraft = false,
  onUndoDraft,
  onCreateNew,
  onSavePreset,
  onDeletePreset,
  drawPreviewFrame,
  getPresetEditorGeometry,
  isRecording,
  canvasDimensions = null,
}) => {
  const previewCanvasRef = useRef(null);
  const primitiveAddMenuRef = useRef(null);
  const textAddMenuRef = useRef(null);
  const presetSnapModeMenuRef = useRef(null);
  const textFontPickerRef = useRef(null);
  const textFontPickerButtonRef = useRef(null);
  const textFontPickerSearchInputRef = useRef(null);
  const wasOpenRef = useRef(false);
  const previousPresetIdRef = useRef(selectedPresetId);
  const autoMaterializedPresetRef = useRef(null);
  const bindingDropPreviewRef = useRef(null);
  const bindingTextMoveAnchorMemoryRef = useRef({});
  const bindingTextMoveBaselineMemoryRef = useRef({});
  const moveAxisLockRef = useRef(null);
  const editingLayerInputRef = useRef(null);
  const cancelLayerRenameRef = useRef(false);
  const [selectionKeys, setSelectionKeys] = useState([]);
  const [dragState, setDragState] = useState(null);
  const [bindingDropPreview, setBindingDropPreview] = useState(null);
  const [isHoveringSelectable, setIsHoveringSelectable] = useState(false);
  const [multiSelectionRotationDeg, setMultiSelectionRotationDeg] = useState(0);
  const [isSnapEnabled, setIsSnapEnabled] = useState(() => (
    readStoredBoolean(PRESET_EDITOR_SNAP_STORAGE_KEY, false)
  ));
  const [snapGridType, setSnapGridType] = useState(() => (
    readStoredString(
      PRESET_EDITOR_SNAP_GRID_TYPE_STORAGE_KEY,
      DEFAULT_SNAP_GRID_TYPE,
      SNAP_GRID_TYPE_OPTIONS.map((option) => option.value),
    )
  ));
  const [isSnapGridVisible, setIsSnapGridVisible] = useState(() => (
    readStoredBoolean(PRESET_EDITOR_SNAP_GRID_VISIBLE_STORAGE_KEY, true)
  ));
  const [presetSnapMode, setPresetSnapMode] = useState(() => (
    readStoredString(
      PRESET_EDITOR_SNAP_MODE_STORAGE_KEY,
      PRESET_SNAP_MODE_GRID_ELEMENTS,
      PRESET_SNAP_MODE_OPTIONS.map((option) => option.value),
    )
  ));
  const [isPresetSnapModeMenuOpen, setIsPresetSnapModeMenuOpen] = useState(false);
  const [snapGuides, setSnapGuides] = useState({ vertical: [], horizontal: [] });
  const [activeLayerPanelId, setActiveLayerPanelId] = useState(null);
  const [draggingLayerId, setDraggingLayerId] = useState(null);
  const [selectedPrimitiveAddType, setSelectedPrimitiveAddType] = useState('rect');
  const [isPrimitiveAddMenuOpen, setIsPrimitiveAddMenuOpen] = useState(false);
  const [selectedTextAddType, setSelectedTextAddType] = useState('main');
  const [isTextAddMenuOpen, setIsTextAddMenuOpen] = useState(false);
  const [textFontSearchQuery, setTextFontSearchQuery] = useState('');
  const [isTextFontPickerOpen, setIsTextFontPickerOpen] = useState(false);
  const [loadedTextFontPreviewFamilies, setLoadedTextFontPreviewFamilies] = useState([]);
  const [isImageImportOpen, setIsImageImportOpen] = useState(false);
  const [replacingImagePrimitiveId, setReplacingImagePrimitiveId] = useState(null);
  const [editingLayerId, setEditingLayerId] = useState(null);
  const [editingLayerName, setEditingLayerName] = useState('');
  const [previewZoomPercent, setPreviewZoomPercent] = useState(PRESET_PREVIEW_ZOOM_DEFAULT);
  const canvasWidth = Math.max(1, Number(canvasDimensions?.width) || CANVAS_WIDTH);
  const canvasHeight = Math.max(1, Number(canvasDimensions?.height) || CANVAS_HEIGHT);
  const canvasAspectRatio = `${canvasWidth} / ${canvasHeight}`;
  const isPortraitPreview = canvasHeight > canvasWidth;
  const previewZoomScale = previewZoomPercent / 100;
  const canZoomOut = previewZoomPercent > PRESET_PREVIEW_ZOOM_MIN;
  const canZoomIn = previewZoomPercent < PRESET_PREVIEW_ZOOM_MAX;
  const staticPreviewStageStyle = useMemo(() => (
    isPortraitPreview
      ? {
        aspectRatio: canvasAspectRatio,
        width: `min(100%, calc(min(40vh, 420px) * ${canvasWidth} / ${canvasHeight}))`,
      }
      : {
        aspectRatio: canvasAspectRatio,
        width: '100%',
      }
  ), [canvasAspectRatio, canvasHeight, canvasWidth, isPortraitPreview]);
  const staticPreviewFrameStyle = useMemo(() => ({
    aspectRatio: canvasAspectRatio,
    width: `${(previewZoomPercent * PRESET_PREVIEW_BASE_FIT_PERCENT) / 100}%`,
  }), [canvasAspectRatio, previewZoomPercent]);
  const staticPreviewContentStyle = useMemo(() => (
    previewZoomScale <= 1
      ? { justifyContent: 'center', alignItems: 'center' }
      : { justifyContent: 'flex-start', alignItems: 'flex-start' }
  ), [previewZoomScale]);
  const dragUndoGroupCounterRef = useRef(0);
  const createDragUndoGroup = useCallback((mode = 'drag') => {
    dragUndoGroupCounterRef.current += 1;
    return `preset-${mode}-${dragUndoGroupCounterRef.current}`;
  }, []);
  const handleZoomOut = useCallback(() => {
    setPreviewZoomPercent((prev) => clamp(prev - PRESET_PREVIEW_ZOOM_STEP, PRESET_PREVIEW_ZOOM_MIN, PRESET_PREVIEW_ZOOM_MAX));
  }, []);
  const handleZoomIn = useCallback(() => {
    setPreviewZoomPercent((prev) => clamp(prev + PRESET_PREVIEW_ZOOM_STEP, PRESET_PREVIEW_ZOOM_MIN, PRESET_PREVIEW_ZOOM_MAX));
  }, []);
  const handleResetZoom = useCallback(() => {
    setPreviewZoomPercent(PRESET_PREVIEW_ZOOM_DEFAULT);
  }, []);
  const clearBindingTextMoveBaselineEntriesByShapeIds = useCallback((shapeIds) => {
    const ids = [...new Set((shapeIds || []).filter(Boolean))];
    if (!ids.length) return;
    const current = bindingTextMoveBaselineMemoryRef.current || {};
    let changed = false;
    const next = { ...current };
    ids.forEach((shapeId) => {
      if (Object.prototype.hasOwnProperty.call(next, shapeId)) {
        delete next[shapeId];
        changed = true;
      }
    });
    if (changed) bindingTextMoveBaselineMemoryRef.current = next;
  }, []);
  const clearBindingTextMoveBaselineEntriesBySourceTextId = useCallback((sourceTextId) => {
    if (!sourceTextId) return;
    const current = bindingTextMoveBaselineMemoryRef.current || {};
    let changed = false;
    const next = {};
    Object.entries(current).forEach(([shapeId, entry]) => {
      const entrySourceIds = Array.isArray(entry?.sourceTextIds)
        ? entry.sourceTextIds
        : (entry?.sourceTextId ? [entry.sourceTextId] : []);
      if (entrySourceIds.includes(sourceTextId)) {
        changed = true;
        return;
      }
      next[shapeId] = entry;
    });
    if (changed) bindingTextMoveBaselineMemoryRef.current = next;
  }, []);

  const shapePrimitives = useMemo(
    () => (Array.isArray(draftPreset?.shapePrimitives) ? draftPreset.shapePrimitives : []),
    [draftPreset?.shapePrimitives],
  );
  const imagePrimitiveEntries = useMemo(
    () => getImagePrimitiveEntries(shapePrimitives),
    [shapePrimitives],
  );
  const imagePrimitiveEntryById = useMemo(
    () => new Map(imagePrimitiveEntries.map((entry) => [entry.primitiveId, entry])),
    [imagePrimitiveEntries],
  );
  const layerOrder = useMemo(
    () => normalizePresetLayerOrder(draftPreset?.layerOrder, shapePrimitives),
    [draftPreset?.layerOrder, shapePrimitives],
  );
  const layerPanelOrder = useMemo(
    () => getPresetLayerPanelOrder(draftPreset?.layerOrder, shapePrimitives),
    [draftPreset?.layerOrder, shapePrimitives],
  );
  const layerLocks = useMemo(
    () => normalizePresetLayerLocks(draftPreset?.layerLocks, shapePrimitives),
    [draftPreset?.layerLocks, shapePrimitives],
  );
  const visibleSnapGrid = useMemo(() => (
    buildCanvasVisibleGridLines({
      width: canvasWidth,
      height: canvasHeight,
      gridType: snapGridType,
    })
  ), [canvasHeight, canvasWidth, snapGridType]);
  const isPresetGridSnapActive = isSnapEnabled && presetSnapMode !== PRESET_SNAP_MODE_ELEMENTS_ONLY;
  const isPresetElementSnapActive = isSnapEnabled && presetSnapMode !== PRESET_SNAP_MODE_GRID_ONLY;
  const showSnapGrid = isPresetGridSnapActive && isSnapGridVisible;

  const geometry = useMemo(() => {
    const fallbackBounds = { minX: 0, minY: 0, maxX: 320, maxY: 120, width: 320, height: 120 };
    if (!draftPreset || typeof getPresetEditorGeometry !== 'function') {
      return {
        renderBounds: fallbackBounds,
        template: {
          bounds: fallbackBounds,
          localBounds: fallbackBounds,
        },
        primitives: [],
        templateBlueprint: null,
        rotation: Number(draftPreset?.rotation || 0),
        rotationPivot: { x: fallbackBounds.width / 2, y: fallbackBounds.height / 2 },
      };
    }

    const nextGeometry = getPresetEditorGeometry(draftPreset) || {};
    const renderBounds = nextGeometry.renderBounds || fallbackBounds;
    const templateBounds = nextGeometry.template?.bounds || null;
    const templateLocalBounds = nextGeometry.template?.localBounds || templateBounds;
    const primitives = Array.isArray(nextGeometry.primitives)
      ? nextGeometry.primitives.map((shape) => ({
        ...shape,
        localBounds: shape?.localBounds || shape?.bounds || null,
        baseBounds: shape?.baseBounds || null,
        contentLocalBounds: shape?.contentLocalBounds || null,
        autoSizeBinding: sanitizeAutoSizeBinding(shape?.autoSizeBinding, shape),
      }))
      : [];
    return {
      renderBounds,
      template: templateBounds
        ? {
          bounds: templateBounds,
          localBounds: templateLocalBounds,
        }
        : null,
      primitives,
      templateBlueprint: nextGeometry.templateBlueprint || null,
      rotation: Number(nextGeometry.rotation || 0),
      rotationPivot: nextGeometry.rotationPivot || { x: renderBounds.width / 2, y: renderBounds.height / 2 },
    };
  }, [draftPreset, getPresetEditorGeometry]);

  useEffect(() => {
    bindingDropPreviewRef.current = bindingDropPreview;
  }, [bindingDropPreview]);

  useEffect(() => {
    if (isOpen) return;
    bindingTextMoveAnchorMemoryRef.current = {};
    bindingTextMoveBaselineMemoryRef.current = {};
    cancelLayerRenameRef.current = false;
    setEditingLayerId(null);
    setEditingLayerName('');
  }, [isOpen]);

  useEffect(() => {
    bindingTextMoveAnchorMemoryRef.current = {};
    bindingTextMoveBaselineMemoryRef.current = {};
    cancelLayerRenameRef.current = false;
    setEditingLayerId(null);
    setEditingLayerName('');
  }, [selectedPresetId]);

  useEffect(() => {
    if (!isRecording) return;
    cancelLayerRenameRef.current = false;
    setEditingLayerId(null);
    setEditingLayerName('');
  }, [isRecording]);

  useEffect(() => {
    if (!editingLayerId || !editingLayerInputRef.current) return;
    editingLayerInputRef.current.focus();
    editingLayerInputRef.current.select();
  }, [editingLayerId]);

  const createPrimitiveFromTemplateBlueprintLayer = useCallback((layer) => {
    const isTextLayer = layer?.layerKind === 'text';
    const isImageLayer = isImagePrimitive(layer);
    const base = isTextLayer
      ? createTextPrimitiveDraft(layer.textSource, layer)
      : { ...createPrimitiveDraft(layer?.type || 'rect'), ...layer };
    return withPrimitiveResetBaseline({
      ...base,
      id: `shape-${Date.now()}-${Math.floor(Math.random() * 1000000)}`,
      type: layer?.type || base.type || 'rect',
      layerKind: isTextLayer ? 'text' : (isImageLayer ? 'image' : 'shape'),
      textSource: normalizeTextSource(layer?.textSource, 'main'),
      textValue: typeof layer?.textValue === 'string' ? layer.textValue : '',
      hideFromPeoplePanel: Boolean(layer?.hideFromPeoplePanel),
      skewX: Number(layer?.skewX || 0),
      fill: isTextLayer || isImageLayer ? false : layer?.fill !== false,
      stroke: isTextLayer || isImageLayer ? false : Boolean(layer?.stroke),
      mainTextVisible: false,
      subTextVisible: false,
      templateFamily: layer?.templateFamily || draftPreset?.preset || null,
      templatePart: layer?.templatePart || null,
    }, { overwrite: true });
  }, [draftPreset?.preset]);

  const buildTemplateMaterializationPatch = useCallback((presetSource, templateBlueprint) => {
    const sourcePreset = presetSource || draftPreset;
    if (!sourcePreset) return null;
    const blueprintLayers = Array.isArray(templateBlueprint?.layers) ? templateBlueprint.layers : [];
    if (!blueprintLayers.length) {
      return {
        templateShapeVisible: false,
        templateMainTextVisible: false,
        templateSubTextVisible: false,
      };
    }

    const sourceShapes = Array.isArray(sourcePreset.shapePrimitives) ? sourcePreset.shapePrimitives : [];
    const nonTemplateShapes = sourceShapes
      .filter((shape) => !isTemplateManagedPrimitive(shape))
      .map((shape) => ({
        ...shape,
        mainTextVisible: false,
        subTextVisible: false,
      }));

    const created = blueprintLayers.map(createPrimitiveFromTemplateBlueprintLayer);
    const templateShapePrimitives = created.filter((shape) => shape.layerKind !== 'text');
    const templateTextPrimitives = created.filter((shape) => shape.layerKind === 'text');
    const nextShapePrimitives = [
      ...templateShapePrimitives,
      ...nonTemplateShapes,
      ...templateTextPrimitives,
    ];

    return {
      shapePrimitives: nextShapePrimitives,
      layerOrder: normalizePresetLayerOrder([], nextShapePrimitives),
      layerLocks: normalizePresetLayerLocks(sourcePreset.layerLocks, nextShapePrimitives),
      templateShapeVisible: false,
      templateMainTextVisible: false,
      templateSubTextVisible: false,
    };
  }, [createPrimitiveFromTemplateBlueprintLayer, draftPreset]);

  const allSelectionKeys = useMemo(() => getAllSelectionKeys(geometry), [geometry]);
  const selectionSignature = useMemo(() => allSelectionKeys.join('|'), [allSelectionKeys]);
  const selectionKeySignature = useMemo(() => selectionKeys.join('|'), [selectionKeys]);

  const selectedPrimitiveId = useMemo(() => {
    const primitiveIds = selectionKeys
      .map((key) => selectionKeyToPrimitiveId(key))
      .filter(Boolean);
    return primitiveIds.length === 1 ? primitiveIds[0] : null;
  }, [selectionKeys]);

  const selectedPrimitive = useMemo(() => {
    if (!selectedPrimitiveId) return null;
    return shapePrimitives.find((shape, index) => primitiveDraftId(shape, index) === selectedPrimitiveId) || null;
  }, [selectedPrimitiveId, shapePrimitives]);
  const replacingImagePrimitive = useMemo(() => {
    if (!replacingImagePrimitiveId) return null;
    return shapePrimitives.find((shape, index) => primitiveDraftId(shape, index) === replacingImagePrimitiveId) || null;
  }, [replacingImagePrimitiveId, shapePrimitives]);
  const replacingImageLockedMode = replacingImagePrimitive?.imageCropMode || null;
  const openReplaceImageImport = useCallback((primitiveId) => {
    if (!primitiveId || isRecording) return;
    setReplacingImagePrimitiveId(primitiveId);
    setIsImageImportOpen(true);
  }, [isRecording]);

  const singleSelectionKey = selectionKeys.length === 1 ? selectionKeys[0] : null;
  const selectionKeyToLayerId = useCallback((selectionKey) => {
    if (!selectionKey) return null;
    if (selectionKey === TEMPLATE_KEY) return TEMPLATE_SHAPE_LAYER_ID;
    const primitiveId = selectionKeyToPrimitiveId(selectionKey);
    return primitiveId ? primitiveLayerId(primitiveId) : null;
  }, []);
  const layerIdToSelectionKey = useCallback((layerId) => {
    if (!layerId) return null;
    if (layerId === TEMPLATE_SHAPE_LAYER_ID) return TEMPLATE_KEY;
    const primitiveId = layerIdToPrimitiveId(layerId);
    return primitiveId ? primitiveSelectionKey(primitiveId) : null;
  }, []);
  const singleSelectionLayerId = useMemo(
    () => selectionKeyToLayerId(singleSelectionKey),
    [selectionKeyToLayerId, singleSelectionKey],
  );
  const isLayerLocked = useCallback((layerId) => Boolean(layerId && layerLocks[layerId]), [layerLocks]);
  const isLayerVisible = useCallback((layerId) => {
    if (!draftPreset || !layerId) return false;
    if (TEMPLATE_BASE_LAYER_IDS.has(layerId)) return false;
    const primitiveId = layerIdToPrimitiveId(layerId);
    if (!primitiveId) return false;
    const primitive = shapePrimitives.find((shape, index) => primitiveDraftId(shape, index) === primitiveId);
    return primitive?.visible !== false;
  }, [draftPreset, shapePrimitives]);
  const recalibrateBoundShapeIds = useCallback(({
    preset,
    shapePrimitives: candidateShapePrimitives,
    targetPrimitiveIds,
  }) => {
    if (!preset || typeof getPresetEditorGeometry !== 'function') {
      return { shapePrimitives: candidateShapePrimitives, geometry: null };
    }
    const uniqueTargetIds = [...new Set((targetPrimitiveIds || []).filter(Boolean))];
    if (!uniqueTargetIds.length || !Array.isArray(candidateShapePrimitives)) {
      return { shapePrimitives: candidateShapePrimitives, geometry: null };
    }

    const targetIdSet = new Set(uniqueTargetIds);
    const basePreset = {
      ...preset,
      shapePrimitives: candidateShapePrimitives,
    };
    let candidateGeometry = getPresetEditorGeometry(basePreset) || null;
    if (!candidateGeometry) {
      return { shapePrimitives: candidateShapePrimitives, geometry: null };
    }
    const geometryTextBoundsById = buildTextBoundsByIdFromGeometry(candidateGeometry);

    let changed = false;
    const nextShapePrimitives = candidateShapePrimitives.map((shape, index) => {
      const primitiveId = primitiveDraftId(shape, index);
      if (!targetIdSet.has(primitiveId)) return shape;
      if (!isAutoSizeBindingTarget(shape)) return shape;
      const binding = sanitizeAutoSizeBinding(shape?.autoSizeBinding, shape);
      if (!binding) return shape;

      const resolvedSourceBounds = resolveBindingSourceBounds({
        binding,
        textBoundsById: geometryTextBoundsById,
      });
      if (!resolvedSourceBounds.unionBounds) return shape;

      const nextBinding = recalibrateAutoSizeBinding({
        shape: { ...shape, autoSizeBinding: binding },
        binding,
        sourceTextBounds: resolvedSourceBounds.unionBounds,
        primarySourceTextBounds: resolvedSourceBounds.primaryBounds || resolvedSourceBounds.unionBounds,
        // Use the edited primitive geometry from the draft shape itself.
        // Rendered geometry has auto-size already applied and would swallow scale edits.
        targetGeometry: null,
      });
      if (!nextBinding) return shape;

      const sameBinding = JSON.stringify(nextBinding) === JSON.stringify(binding);
      if (sameBinding) {
        if (shape.autoSizeBinding === binding) return shape;
        changed = true;
        return { ...shape, autoSizeBinding: binding };
      }

      changed = true;
      return { ...shape, autoSizeBinding: nextBinding };
    });

    if (!changed) {
      return { shapePrimitives: candidateShapePrimitives, geometry: candidateGeometry };
    }

    const recalibratedPreset = {
      ...preset,
      shapePrimitives: nextShapePrimitives,
    };
    candidateGeometry = getPresetEditorGeometry(recalibratedPreset) || candidateGeometry;
    return { shapePrimitives: nextShapePrimitives, geometry: candidateGeometry };
  }, [getPresetEditorGeometry]);
  const resolveBoundResizeGestureForScaleDrag = useCallback(({
    startConfig,
    startGeometry,
    selectionKeys: selectedKeys,
    handleId,
  }) => {
    if (!startConfig || !startGeometry || !handleId) return null;
    const normalizedSelection = sanitizeSelectionKeys(selectedKeys || [], getAllSelectionKeys(startGeometry));
    if (normalizedSelection.length !== 1 || normalizedSelection[0] === TEMPLATE_KEY) return null;

    const primitiveId = selectionKeyToPrimitiveId(normalizedSelection[0]);
    if (!primitiveId) return null;

    const primitive = findPrimitiveById(startConfig.shapePrimitives, primitiveId);
    if (!primitive) return null;
    const binding = sanitizeAutoSizeBinding(primitive.autoSizeBinding, primitive);
    if (!binding) return null;

    const sourceTextGeometry = findGeometryPrimitiveById(startGeometry, binding.sourceTextId);
    return resolveBoundResizeGesture({
      selectedPrimitiveId: primitiveId,
      handleId,
      primitive,
      sourceTextGeometry,
    });
  }, []);
  const buildBoundResizePatchForScaleDrag = useCallback(({
    point,
    dragInfo,
  }) => {
    if (!point || !dragInfo || dragInfo.mode !== 'scale') return null;
    if (!dragInfo.boundResize?.targetPrimitiveId || !dragInfo.handleId) return null;

    const targetPrimitiveId = dragInfo.boundResize.targetPrimitiveId;
    const startShape = findPrimitiveById(dragInfo.startConfig?.shapePrimitives, targetPrimitiveId);
    const binding = sanitizeAutoSizeBinding(startShape?.autoSizeBinding, startShape);
    if (!startShape || !binding) return null;
    const resolvedSourceBounds = resolveBindingSourceBounds({
      binding,
      textBoundsById: buildTextBoundsByIdFromGeometry(dragInfo.startGeometry),
    });
    if (!resolvedSourceBounds.unionBounds) return null;

    const deltaVector = {
      x: point.x - dragInfo.startPoint.x,
      y: point.y - dragInfo.startPoint.y,
    };
    const deltaAxes = projectVectorToAxes(deltaVector, dragInfo.scaleAxesRotateDeg || 0);
    const resizedShape = applyBoundShapeHandleResize({
      shape: startShape,
      binding,
      sourceTextBounds: resolvedSourceBounds.unionBounds,
      handleId: dragInfo.handleId,
      deltaAxes,
      canvasWidth,
      canvasHeight,
    });
    if (!resizedShape) return null;

    const unchanged = (
      Number(resizedShape.x) === Number(startShape.x)
      && Number(resizedShape.y) === Number(startShape.y)
      && Number(resizedShape.width) === Number(startShape.width)
      && Number(resizedShape.height) === Number(startShape.height)
    );
    if (unchanged) return {};

    const nextShapePrimitives = patchShapePrimitiveById(
      dragInfo.startConfig.shapePrimitives,
      targetPrimitiveId,
      () => resizedShape,
    );
    if (!nextShapePrimitives) return null;
    return { shapePrimitives: nextShapePrimitives };
  }, [canvasHeight, canvasWidth]);
  const buildBindingDropPreviewForMove = useCallback(({
    point,
    dragInfo,
    candidatePreset,
    candidateGeometry,
  }) => {
    if (!point || !dragInfo || dragInfo.mode !== 'move') return null;
    if (!candidatePreset || !candidateGeometry) return null;

    const selectedPrimitiveIds = (dragInfo.selectionKeys || [])
      .map((key) => selectionKeyToPrimitiveId(key))
      .filter(Boolean);
    if (selectedPrimitiveIds.length !== 1) return null;
    const sourceTextId = selectedPrimitiveIds[0];
    const sourcePrimitive = findPrimitiveById(candidatePreset.shapePrimitives, sourceTextId);
    if (!isAutoSizeBindingSource(sourcePrimitive)) return null;

    const sourceGeometry = findGeometryPrimitiveById(candidateGeometry, sourceTextId);
    if (!sourceGeometry?.contentLocalBounds) return null;
    const geometryTextBoundsById = buildTextBoundsByIdFromGeometry(candidateGeometry);

    const localX = point.x - Number(candidatePreset.posX || 0);
    const localY = point.y - Number(candidatePreset.posY || 0);
    const candidateLayerOrder = normalizePresetLayerOrder(candidatePreset.layerOrder, candidatePreset.shapePrimitives);

    for (let index = candidateLayerOrder.length - 1; index >= 0; index -= 1) {
      const layerId = candidateLayerOrder[index];
      if (TEMPLATE_BASE_LAYER_IDS.has(layerId)) continue;
      if (Boolean(layerLocks[layerId])) continue;

      const primitiveId = layerIdToPrimitiveId(layerId);
      if (!primitiveId || primitiveId === sourceTextId) continue;
      const targetShape = findPrimitiveById(candidatePreset.shapePrimitives, primitiveId);
      if (!targetShape || targetShape.visible === false || !isAutoSizeBindingTarget(targetShape)) continue;

      const targetGeometry = findGeometryPrimitiveById(candidateGeometry, primitiveId);
      if (!targetGeometry?.bounds) continue;
      if (!pointInBounds(localX, localY, targetGeometry.bounds)) continue;

      const existingBinding = sanitizeAutoSizeBinding(targetShape.autoSizeBinding, targetShape);
      const existingSourceIds = existingBinding ? getAutoSizeBindingSourceIds(existingBinding) : [];
      if (existingSourceIds.includes(sourceTextId)) return null;
      const nextSourceTextIds = existingBinding
        ? [...existingSourceIds, sourceTextId]
        : [sourceTextId];
      const primarySourceTextId = existingBinding?.sourceTextId || sourceTextId;
      const sourceBoundsResolution = resolveBindingSourceBounds({
        binding: {
          ...(existingBinding || {}),
          sourceTextId: primarySourceTextId,
          sourceTextIds: nextSourceTextIds,
        },
        textBoundsById: geometryTextBoundsById,
      });
      if (!sourceBoundsResolution.unionBounds) return null;

      const capturedBinding = captureAutoSizeBinding({
        sourceTextBounds: sourceBoundsResolution.unionBounds,
        primarySourceTextBounds: sourceBoundsResolution.primaryBounds || sourceBoundsResolution.unionBounds,
        targetPrimitive: targetShape,
        targetGeometry: { baseBounds: targetGeometry.baseBounds || null },
        sourceTextId: primarySourceTextId,
        sourceTextIds: nextSourceTextIds,
      });
      if (!capturedBinding) return null;

      const nextShapePrimitives = patchShapePrimitiveById(
        candidatePreset.shapePrimitives,
        primitiveId,
        { autoSizeBinding: capturedBinding },
      );
      if (!nextShapePrimitives) return null;

      return {
        sourceTextId,
        targetShapeId: primitiveId,
        targetBounds: targetGeometry.bounds,
        point,
        action: existingBinding ? 'append' : 'bind',
        label: existingBinding ? 'Add auto-size link' : 'Bind auto-size',
        commitPatch: { shapePrimitives: nextShapePrimitives },
      };
    }

    return null;
  }, [layerLocks]);
  const expandMoveDragSelectionWithLinkedTextSources = useCallback((selectionKeysToExpand) => {
    const baseSelection = sanitizeSelectionKeys(selectionKeysToExpand || [], allSelectionKeys);
    if (!baseSelection.length) return baseSelection;

    let changed = false;
    const seenSelectionKeys = new Set(baseSelection);
    const seenPrimitiveIds = new Set(
      baseSelection
        .map((key) => selectionKeyToPrimitiveId(key))
        .filter(Boolean),
    );
    const nextSelection = [...baseSelection];

    for (const key of baseSelection) {
      const primitiveId = selectionKeyToPrimitiveId(key);
      if (!primitiveId) continue;
      const primitive = findPrimitiveById(shapePrimitives, primitiveId);
      const binding = sanitizeAutoSizeBinding(primitive?.autoSizeBinding, primitive);
      if (!binding) continue;

      const sourceIds = getAutoSizeBindingSourceIds(binding);
      for (const sourceId of sourceIds) {
        if (!sourceId || seenPrimitiveIds.has(sourceId)) continue;
        const sourceTextPrimitive = findPrimitiveById(shapePrimitives, sourceId);
        if (!isAutoSizeBindingSource(sourceTextPrimitive)) continue;
        const sourceLayerId = primitiveLayerId(sourceId);
        if (!sourceLayerId || isLayerLocked(sourceLayerId) || !isLayerVisible(sourceLayerId)) continue;

        const sourceSelectionKey = primitiveSelectionKey(sourceId);
        if (!sourceSelectionKey || seenSelectionKeys.has(sourceSelectionKey)) continue;
        seenSelectionKeys.add(sourceSelectionKey);
        seenPrimitiveIds.add(sourceId);
        nextSelection.push(sourceSelectionKey);
        changed = true;
      }
    }

    return changed ? sanitizeSelectionKeys(nextSelection, allSelectionKeys) : baseSelection;
  }, [allSelectionKeys, isLayerLocked, isLayerVisible, shapePrimitives]);
  const reshapeBindingsForMovedTextSelection = useCallback(({
    dragInfo,
    candidatePreset,
    candidateGeometry,
  }) => {
    const isSupportedDragMode = dragInfo && (dragInfo.mode === 'move' || dragInfo.mode === 'scale');
    if (!isSupportedDragMode) {
      return { shapePrimitives: candidatePreset?.shapePrimitives, anchorsByTextId: dragInfo?.bindingTextMoveAnchors || null, changed: false };
    }
    if (!candidatePreset || !candidateGeometry || !Array.isArray(candidatePreset.shapePrimitives)) {
      return { shapePrimitives: candidatePreset?.shapePrimitives, anchorsByTextId: dragInfo.bindingTextMoveAnchors || null, changed: false };
    }

    const selectedPrimitiveIds = (dragInfo.selectionKeys || [])
      .map((key) => selectionKeyToPrimitiveId(key))
      .filter(Boolean);
    if (!selectedPrimitiveIds.length) {
      return { shapePrimitives: candidatePreset.shapePrimitives, anchorsByTextId: dragInfo.bindingTextMoveAnchors || null, changed: false };
    }
    const selectedPrimitiveIdSet = new Set(selectedPrimitiveIds);

    const startShapes = Array.isArray(dragInfo.startConfig?.shapePrimitives)
      ? dragInfo.startConfig.shapePrimitives
      : [];
    const startShapeMap = new Map(startShapes.map((shape, index) => [primitiveDraftId(shape, index), shape]));
    const autoIncludedTextIdSet = new Set(dragInfo.autoIncludedBindingSourceTextIds || []);
    const movedTextIds = selectedPrimitiveIds.filter((primitiveId) => (
      isAutoSizeBindingSource(startShapeMap.get(primitiveId))
      && !autoIncludedTextIdSet.has(primitiveId)
    ));
    if (!movedTextIds.length) {
      return { shapePrimitives: candidatePreset.shapePrimitives, anchorsByTextId: dragInfo.bindingTextMoveAnchors || null, changed: false };
    }
    const movedTextIdSet = new Set(movedTextIds);
    const startTextBoundsById = buildTextBoundsByIdFromGeometry(dragInfo.startGeometry);
    const nextTextBoundsById = buildTextBoundsByIdFromGeometry(candidateGeometry);

    let anchorsByTextId = { ...(dragInfo.bindingTextMoveAnchors || {}) };
    let anchorsChanged = false;
    let changed = false;
    let nextShapePrimitives = candidatePreset.shapePrimitives;
    const processedShapeIds = new Set();

    for (const textId of movedTextIds) {
      const startTextBounds = startTextBoundsById.get(textId) || null;
      const nextTextBounds = nextTextBoundsById.get(textId) || null;
      if (!startTextBounds || !nextTextBounds) continue;

      const deltaX = Number(nextTextBounds.minX || 0) - Number(startTextBounds.minX || 0);
      const deltaY = Number(nextTextBounds.minY || 0) - Number(startTextBounds.minY || 0);
      const currentAnchors = anchorsByTextId[textId] ? { ...anchorsByTextId[textId] } : {};
      if (dragInfo.mode === 'move' && !currentAnchors.x && Math.abs(deltaX) > 0.25) {
        currentAnchors.x = deltaX >= 0 ? 'left' : 'right';
        anchorsChanged = true;
      }
      if (dragInfo.mode === 'move' && !currentAnchors.y && Math.abs(deltaY) > 0.25) {
        currentAnchors.y = deltaY >= 0 ? 'top' : 'bottom';
        anchorsChanged = true;
      }
      if (Object.keys(currentAnchors).length > 0) {
        anchorsByTextId[textId] = currentAnchors;
      }

      const remappedShapePrimitives = [];
      for (let shapeIndex = 0; shapeIndex < nextShapePrimitives.length; shapeIndex += 1) {
        const shape = nextShapePrimitives[shapeIndex];
        const primitiveId = primitiveDraftId(shape, shapeIndex);
        let nextShape = shape;

        if (!processedShapeIds.has(primitiveId) && !selectedPrimitiveIdSet.has(primitiveId)) {
          const startShape = startShapeMap.get(primitiveId);
          if (startShape) {
            const startBinding = sanitizeAutoSizeBinding(startShape?.autoSizeBinding, startShape);
            if (startBinding && bindingReferencesSourceTextId(startBinding, textId)) {
              const bindingSourceIds = getAutoSizeBindingSourceIds(startBinding);
              const linkedMovedSourceIds = bindingSourceIds.filter((id) => movedTextIdSet.has(id));
              if (!(linkedMovedSourceIds.length > 1 && linkedMovedSourceIds[0] !== textId)) {
                const startResolvedSourceBounds = resolveBindingSourceBounds({
                  binding: startBinding,
                  textBoundsById: startTextBoundsById,
                });
                const nextResolvedSourceBounds = resolveBindingSourceBounds({
                  binding: startBinding,
                  textBoundsById: nextTextBoundsById,
                });
                const aggregateStartBounds = startResolvedSourceBounds.unionBounds || null;
                const aggregateNextBounds = nextResolvedSourceBounds.unionBounds || null;

                if (aggregateStartBounds && aggregateNextBounds) {
                  const startTargetGeometry = findGeometryPrimitiveById(dragInfo.startGeometry, primitiveId);
                  const currentBaselineEntries = bindingTextMoveBaselineMemoryRef.current || {};
                  let baselineEntry = currentBaselineEntries[primitiveId];
                  const baselineNeedsInit = !baselineEntry
                    || baselineEntry.sourceTextId !== startBinding.sourceTextId
                    || JSON.stringify(baselineEntry.sourceTextIds || []) !== JSON.stringify(bindingSourceIds)
                    || baselineEntry.mode !== startBinding.mode
                    || !baselineEntry.targetBounds;
                  if (baselineNeedsInit) {
                    baselineEntry = {
                      sourceTextId: startBinding.sourceTextId,
                      sourceTextIds: bindingSourceIds,
                      mode: startBinding.mode,
                      targetBounds: startTargetGeometry?.baseBounds || null,
                      binding: startBinding,
                    };
                    bindingTextMoveBaselineMemoryRef.current = {
                      ...currentBaselineEntries,
                      [primitiveId]: baselineEntry,
                    };
                  }
                  const isMultiSource = bindingSourceIds.length > 1;
                  const nextBinding = adjustAutoSizeBindingForTextMove({
                    shape,
                    binding: startBinding,
                    startTextBounds: isMultiSource ? aggregateStartBounds : startTextBounds,
                    nextTextBounds: isMultiSource ? aggregateNextBounds : nextTextBounds,
                    anchors: isMultiSource ? null : currentAnchors,
                    referenceTargetBounds: (startBinding.mode === 'box' || startBinding.mode === 'line')
                      ? (baselineEntry?.targetBounds || null)
                      : null,
                    referenceBinding: (startBinding.mode === 'box' || startBinding.mode === 'line')
                      ? (baselineEntry?.binding || startBinding)
                      : null,
                  });
                  if (nextBinding) {
                    const sameBinding = JSON.stringify(nextBinding) === JSON.stringify(sanitizeAutoSizeBinding(shape?.autoSizeBinding, shape));
                    if (!sameBinding) {
                      processedShapeIds.add(primitiveId);
                      changed = true;
                      nextShape = {
                        ...shape,
                        autoSizeBinding: nextBinding,
                      };
                    }
                  }
                }
              }
            }
          }
        }

        remappedShapePrimitives.push(nextShape);
      }
      nextShapePrimitives = remappedShapePrimitives;
    }

    return {
      shapePrimitives: nextShapePrimitives,
      anchorsByTextId,
      changed: changed || anchorsChanged,
      anchorsChanged,
    };
  }, []);
  const singleSelectionRotationDeg = useMemo(() => {
    if (!draftPreset || !singleSelectionKey) return 0;
    const templateRotation = Number(draftPreset.rotation || 0);
    if (singleSelectionKey === TEMPLATE_KEY) return normalizeAngle(templateRotation);
    if (selectedPrimitive) {
      const primitiveRotation = Number(selectedPrimitive.rotation || 0);
      return normalizeAngle(primitiveRotation + templateRotation);
    }
    return 0;
  }, [draftPreset, selectedPrimitive, singleSelectionKey]);

  const selectedBoundsWorld = useMemo(
    () => (draftPreset ? getSelectionBoundsWorld(geometry, draftPreset, selectionKeys) : null),
    [draftPreset, geometry, selectionKeys],
  );

  const isMultiSelection = selectionKeys.length > 1;
  const frameRotateDeg = useMemo(() => {
    if (dragState?.mode === 'rotate') return Number(dragState.previewRotateDeg || 0);
    if (isMultiSelection) return Number(multiSelectionRotationDeg || 0);
    return singleSelectionRotationDeg;
  }, [dragState, isMultiSelection, multiSelectionRotationDeg, singleSelectionRotationDeg]);
  const hasRotatedFrame = Math.abs(frameRotateDeg) > 0.01;
  const selectionBoundsForTransformBox = useMemo(() => {
    if (hasRotatedFrame && draftPreset) {
      const rotatedFrameBounds = getSelectionFrameBoundsWorld({
        geometry,
        config: draftPreset,
        selectionKeys,
        frameRotateDeg,
      });
      if (rotatedFrameBounds) return rotatedFrameBounds;
    }
    if (dragState?.mode === 'rotate' && dragState.startBoundsWorld) return dragState.startBoundsWorld;
    return selectedBoundsWorld;
  }, [draftPreset, dragState, frameRotateDeg, geometry, hasRotatedFrame, selectedBoundsWorld, selectionKeys]);

  const selectedBoundsStyle = useMemo(() => {
    if (!selectionBoundsForTransformBox) return null;
    const nextStyle = {
      left: `${(selectionBoundsForTransformBox.minX / canvasWidth) * 100}%`,
      top: `${(selectionBoundsForTransformBox.minY / canvasHeight) * 100}%`,
      width: `${(selectionBoundsForTransformBox.width / canvasWidth) * 100}%`,
      height: `${(selectionBoundsForTransformBox.height / canvasHeight) * 100}%`,
    };
    if (hasRotatedFrame) {
      nextStyle.transform = `rotate(${frameRotateDeg.toFixed(2)}deg)`;
      nextStyle.transformOrigin = 'center center';
    }
    return nextStyle;
  }, [canvasHeight, canvasWidth, frameRotateDeg, hasRotatedFrame, selectionBoundsForTransformBox]);

  const hasActiveSelection = selectionKeys.length > 0;
  const hasLockedSelection = useMemo(
    () => selectionKeys.some((key) => isLayerLocked(selectionKeyToLayerId(key))),
    [isLayerLocked, selectionKeyToLayerId, selectionKeys],
  );
  const hasPrimitiveOnlySelection = useMemo(
    () => selectionKeys.length > 0
      && selectionKeys.every((key) => Boolean(selectionKeyToPrimitiveId(key))),
    [selectionKeys],
  );
  const canvasCursorClass = dragState && dragState.mode !== 'marquee'
    ? 'cursor-grabbing'
    : (isHoveringSelectable ? 'cursor-grab' : 'cursor-default');

  const selectedPrimitiveColorDraft = useMemo(
    () => resolvePrimitiveColorDraft(selectedPrimitive),
    [selectedPrimitive],
  );
  const selectedPrimitiveAddOption = useMemo(
    () => PRIMITIVE_ADD_OPTIONS.find((option) => option.value === selectedPrimitiveAddType) || PRIMITIVE_ADD_OPTIONS[1],
    [selectedPrimitiveAddType],
  );
  const selectedTextAddOption = useMemo(
    () => TEXT_ADD_OPTIONS.find((option) => option.value === selectedTextAddType) || TEXT_ADD_OPTIONS[0],
    [selectedTextAddType],
  );
  const selectedPrimitiveIsTextLayer = selectedPrimitive?.layerKind === 'text';
  const selectedPrimitiveTextSource = normalizeTextSource(selectedPrimitive?.textSource, 'main');
  const selectedPrimitiveIsGenericTextLayer = selectedPrimitiveIsTextLayer && selectedPrimitiveTextSource === 'generic';
  const selectedPrimitiveIsImageLayer = isImagePrimitive(selectedPrimitive);
  const selectedPrimitiveTextFontFamily = useMemo(
    () => sanitizeTextLayerFontFamily(selectedPrimitive?.textFontFamily),
    [selectedPrimitive],
  );
  const deletePrimitivesByIds = useCallback((primitiveIds) => {
    if (!draftPreset) return;
    const normalizedPrimitiveIds = [...new Set((Array.isArray(primitiveIds) ? primitiveIds : []).filter(Boolean))];
    if (!normalizedPrimitiveIds.length) return;
    const primitiveIdSet = new Set(normalizedPrimitiveIds);
    const filteredShapePrimitives = shapePrimitives.filter(
      (shape, index) => !primitiveIdSet.has(primitiveDraftId(shape, index)),
    );
    if (filteredShapePrimitives.length === shapePrimitives.length) return;

    clearBindingTextMoveBaselineEntriesByShapeIds(normalizedPrimitiveIds);
    normalizedPrimitiveIds.forEach((primitiveId) => {
      clearBindingTextMoveBaselineEntriesBySourceTextId(primitiveId);
    });

    let nextShapePrimitives = filteredShapePrimitives;
    normalizedPrimitiveIds.forEach((primitiveId) => {
      nextShapePrimitives = clearBindingsReferencingTextId(nextShapePrimitives, primitiveId);
    });

    onDraftChange({
      shapePrimitives: nextShapePrimitives,
      layerOrder: normalizePresetLayerOrder(draftPreset.layerOrder, nextShapePrimitives),
      layerLocks: normalizePresetLayerLocks(draftPreset.layerLocks, nextShapePrimitives),
    });
    if (activeLayerPanelId && primitiveIdSet.has(layerIdToPrimitiveId(activeLayerPanelId))) {
      setActiveLayerPanelId(null);
    }
    setSelectionKeys([]);
  }, [
    activeLayerPanelId,
    clearBindingTextMoveBaselineEntriesByShapeIds,
    clearBindingTextMoveBaselineEntriesBySourceTextId,
    draftPreset,
    onDraftChange,
    shapePrimitives,
  ]);
  const loadedTextFontPreviewFamilySet = useMemo(
    () => new Set(loadedTextFontPreviewFamilies),
    [loadedTextFontPreviewFamilies],
  );
  const textFontSearchResults = useMemo(() => (
    textFontSearchQuery.trim()
      ? getTextLayerFontSearchResults(textFontSearchQuery, {
        limit: TEXT_LAYER_FONT_SEARCH_RESULT_LIMIT,
      })
      : { families: [], hasMore: false, totalMatches: 0 }
  ), [textFontSearchQuery]);
  const visibleTextLayerFontFamilies = useMemo(() => {
    if (textFontSearchQuery.trim()) return textFontSearchResults.families;
    return mergeUniqueFamilies(
      selectedPrimitiveTextFontFamily ? [selectedPrimitiveTextFontFamily] : [],
      TEXT_LAYER_FONT_PICKER_DEFAULT_FAMILIES,
    );
  }, [selectedPrimitiveTextFontFamily, textFontSearchQuery, textFontSearchResults.families]);
  const textFontSearchHasMoreResults = textFontSearchResults.hasMore;
  const textFontSearchTotalMatches = textFontSearchResults.totalMatches;
  const textFontPickerPreviewFamilies = useMemo(() => {
    if (!isTextFontPickerOpen || !selectedPrimitiveIsTextLayer) return [];
    return mergeUniqueFamilies(
      TEXT_LAYER_FONT_PICKER_DEFAULT_FAMILIES,
      selectedPrimitiveTextFontFamily ? [selectedPrimitiveTextFontFamily] : [],
      textFontSearchQuery.trim() ? visibleTextLayerFontFamilies.slice(0, TEXT_FONT_SEARCH_PREVIEW_LIMIT) : [],
    );
  }, [
    isTextFontPickerOpen,
    selectedPrimitiveIsTextLayer,
    selectedPrimitiveTextFontFamily,
    textFontSearchQuery,
    visibleTextLayerFontFamilies,
  ]);
  const selectedTextFontPickerLabel = selectedPrimitiveTextFontFamily || TEXT_FONT_DEFAULT_OPTION_LABEL;
  const textFontPickerDropdownId = selectedPrimitiveId
    ? `preset-overlay-text-font-options-${selectedPrimitiveId}`
    : 'preset-overlay-text-font-options';
  const selectedTextFontPickerPreviewStyle = selectedPrimitiveTextFontFamily
    && loadedTextFontPreviewFamilySet.has(selectedPrimitiveTextFontFamily)
    ? { fontFamily: buildFontPreviewFamily(selectedPrimitiveTextFontFamily) }
    : undefined;

  useEffect(() => {
    setTextFontSearchQuery('');
    setIsTextFontPickerOpen(false);
  }, [selectedPrimitiveId, selectedPrimitiveIsTextLayer]);

  const selectedPrimitiveCanFill = selectedPrimitive && !selectedPrimitiveIsTextLayer && selectedPrimitive.type !== 'line';
  const selectedPrimitiveShowsStrokeSection = Boolean(selectedPrimitive) && !selectedPrimitiveIsTextLayer && !selectedPrimitiveIsImageLayer;
  const selectedPrimitiveCanStroke = Boolean(selectedPrimitive) && (
    !selectedPrimitiveIsTextLayer && (selectedPrimitive.type === 'line' || Boolean(selectedPrimitive.stroke))
  );
  const fillControlsDisabled = Boolean(selectedPrimitiveCanFill && selectedPrimitive?.fill === false);
  const strokeControlsDisabled = Boolean(selectedPrimitive?.type !== 'line' && !selectedPrimitiveCanStroke);

  const selectedBoundsLocal = useMemo(() => {
    if (!selectionBoundsForTransformBox || !draftPreset) return null;
    return {
      minX: selectionBoundsForTransformBox.minX - draftPreset.posX,
      minY: selectionBoundsForTransformBox.minY - draftPreset.posY,
      maxX: selectionBoundsForTransformBox.maxX - draftPreset.posX,
      maxY: selectionBoundsForTransformBox.maxY - draftPreset.posY,
      width: selectionBoundsForTransformBox.width,
      height: selectionBoundsForTransformBox.height,
    };
  }, [draftPreset, selectionBoundsForTransformBox]);

  const selectedBoundsForCanvasOverlay = useMemo(() => {
    if (!selectedBoundsLocal) return null;
    if (dragState?.mode === 'rotate' || hasRotatedFrame) return null;
    return {
      minX: selectedBoundsLocal.minX + draftPreset.posX,
      minY: selectedBoundsLocal.minY + draftPreset.posY,
      maxX: selectedBoundsLocal.maxX + draftPreset.posX,
      maxY: selectedBoundsLocal.maxY + draftPreset.posY,
      width: selectedBoundsLocal.width,
      height: selectedBoundsLocal.height,
    };
  }, [draftPreset, dragState, hasRotatedFrame, selectedBoundsLocal]);
  const selectedTransformLayerLocked = useMemo(
    () => Boolean(singleSelectionLayerId && isLayerLocked(singleSelectionLayerId)),
    [isLayerLocked, singleSelectionLayerId],
  );
  const selectedPrimitiveControlsDisabled = isRecording || selectedTransformLayerLocked;

  useEffect(() => {
    if (!selectedPrimitiveIsTextLayer || selectedPrimitiveControlsDisabled) {
      setIsTextFontPickerOpen(false);
    }
  }, [selectedPrimitiveControlsDisabled, selectedPrimitiveIsTextLayer]);

  useEffect(() => {
    if (!isTextFontPickerOpen) return;
    textFontPickerSearchInputRef.current?.focus();
    textFontPickerSearchInputRef.current?.select();
  }, [isTextFontPickerOpen]);

  useEffect(() => {
    if (!selectedPrimitiveIsTextLayer || !isTextFontPickerOpen || !textFontPickerPreviewFamilies.length) return undefined;
    let cancelled = false;
    Promise.resolve(ensureGoogleFontsLoaded(textFontPickerPreviewFamilies)).then((results) => {
      if (cancelled) return;
      const loadedFamilies = textFontPickerPreviewFamilies.filter((family, index) => results?.[index] !== false);
      if (!loadedFamilies.length) return;
      setLoadedTextFontPreviewFamilies((current) => {
        const nextFamilies = mergeUniqueFamilies(current, loadedFamilies);
        return nextFamilies.length === current.length ? current : nextFamilies;
      });
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isTextFontPickerOpen, selectedPrimitiveIsTextLayer, textFontPickerPreviewFamilies]);

  const marqueeBoundsStyle = useMemo(() => {
    if (!dragState || dragState.mode !== 'marquee' || !dragState.crossedThreshold) return null;
    const marqueeBounds = normalizeMarqueeBounds(dragState.startPoint, dragState.currentPoint);
    if (!marqueeBounds) return null;
    return {
      left: `${(marqueeBounds.minX / canvasWidth) * 100}%`,
      top: `${(marqueeBounds.minY / canvasHeight) * 100}%`,
      width: `${(marqueeBounds.width / canvasWidth) * 100}%`,
      height: `${(marqueeBounds.height / canvasHeight) * 100}%`,
    };
  }, [canvasHeight, canvasWidth, dragState]);
  const bindingDropPreviewBoxStyle = useMemo(() => {
    if (!bindingDropPreview?.targetBounds || !draftPreset) return null;
    const bounds = bindingDropPreview.targetBounds;
    return {
      left: `${((bounds.minX + draftPreset.posX) / canvasWidth) * 100}%`,
      top: `${((bounds.minY + draftPreset.posY) / canvasHeight) * 100}%`,
      width: `${(bounds.width / canvasWidth) * 100}%`,
      height: `${(bounds.height / canvasHeight) * 100}%`,
    };
  }, [bindingDropPreview, canvasHeight, canvasWidth, draftPreset]);
  const bindingDropPreviewChipStyle = useMemo(() => {
    if (!bindingDropPreview || !draftPreset) return null;
    const point = bindingDropPreview.point || null;
    if (point) {
      const left = clamp(point.x + 14, 8, canvasWidth - 8);
      const top = clamp(point.y - 18, 8, canvasHeight - 8);
      return {
        left: `${(left / canvasWidth) * 100}%`,
        top: `${(top / canvasHeight) * 100}%`,
      };
    }
    const bounds = bindingDropPreview.targetBounds;
    if (!bounds) return null;
    const left = bounds.minX + draftPreset.posX + 8;
    const top = bounds.minY + draftPreset.posY + 8;
    return {
      left: `${(clamp(left, 8, canvasWidth - 8) / canvasWidth) * 100}%`,
      top: `${(clamp(top, 8, canvasHeight - 8) / canvasHeight) * 100}%`,
    };
  }, [bindingDropPreview, canvasHeight, canvasWidth, draftPreset]);

  useEffect(() => {
    if (!isOpen || !draftPreset) return undefined;
    const onKeyDown = (event) => {
      const isUndoShortcut = (
        (event.ctrlKey || event.metaKey)
        && !event.shiftKey
        && !event.altKey
        && typeof event.key === 'string'
        && event.key.toLowerCase() === 'z'
      );
      if (isUndoShortcut) {
        if (
          canUndoDraft
          && !isImageImportOpen
          && !dragState
          && !isRecording
          && !isEditableKeyTarget(event.target)
        ) {
          event.preventDefault();
          onUndoDraft?.();
        }
        return;
      }
      if (event.key === 'Delete') {
        const selectedPrimitiveIds = hasPrimitiveOnlySelection
          ? selectionKeys.map((key) => selectionKeyToPrimitiveId(key)).filter(Boolean)
          : [];
        if (
          selectedPrimitiveIds.length
          && !isImageImportOpen
          && !dragState
          && !isRecording
          && !hasLockedSelection
          && !isEditableKeyTarget(event.target)
        ) {
          event.preventDefault();
          deletePrimitivesByIds(selectedPrimitiveIds);
        }
        return;
      }
      if (event.key !== 'Escape') return;
      if (isTextFontPickerOpen) {
        event.preventDefault();
        setIsTextFontPickerOpen(false);
        textFontPickerButtonRef.current?.focus();
        return;
      }
      if (isPrimitiveAddMenuOpen) {
        setIsPrimitiveAddMenuOpen(false);
        return;
      }
      if (isTextAddMenuOpen) {
        setIsTextAddMenuOpen(false);
        return;
      }
      if (isPresetSnapModeMenuOpen) {
        setIsPresetSnapModeMenuOpen(false);
        return;
      }
      if (!isImageImportOpen) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    canUndoDraft,
    deletePrimitivesByIds,
    dragState,
    draftPreset,
    hasLockedSelection,
    hasPrimitiveOnlySelection,
    isImageImportOpen,
    isOpen,
    isPresetSnapModeMenuOpen,
    isPrimitiveAddMenuOpen,
    isRecording,
    isTextFontPickerOpen,
    isTextAddMenuOpen,
    onClose,
    onUndoDraft,
    selectionKeys,
  ]);

  useEffect(() => {
    if (!isOpen || (!isPrimitiveAddMenuOpen && !isTextAddMenuOpen)) return undefined;
    const onMouseDown = (event) => {
      const target = event.target;
      const inPrimitive = primitiveAddMenuRef.current && primitiveAddMenuRef.current.contains(target);
      const inText = textAddMenuRef.current && textAddMenuRef.current.contains(target);
      if (inPrimitive || inText) return;
      if (isPrimitiveAddMenuOpen) setIsPrimitiveAddMenuOpen(false);
      if (isTextAddMenuOpen) setIsTextAddMenuOpen(false);
    };
    window.addEventListener('mousedown', onMouseDown);
    return () => window.removeEventListener('mousedown', onMouseDown);
  }, [isOpen, isPrimitiveAddMenuOpen, isTextAddMenuOpen]);

  useEffect(() => {
    if (!isOpen || !isPresetSnapModeMenuOpen) return undefined;
    const onMouseDown = (event) => {
      const target = event.target;
      if (presetSnapModeMenuRef.current && presetSnapModeMenuRef.current.contains(target)) return;
      setIsPresetSnapModeMenuOpen(false);
    };
    window.addEventListener('mousedown', onMouseDown);
    return () => window.removeEventListener('mousedown', onMouseDown);
  }, [isOpen, isPresetSnapModeMenuOpen]);

  useEffect(() => {
    if (!isOpen || !isTextFontPickerOpen) return undefined;
    const onMouseDown = (event) => {
      const target = event.target;
      if (textFontPickerRef.current && textFontPickerRef.current.contains(target)) return;
      setIsTextFontPickerOpen(false);
    };
    window.addEventListener('mousedown', onMouseDown);
    return () => window.removeEventListener('mousedown', onMouseDown);
  }, [isOpen, isTextFontPickerOpen]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      window.localStorage.setItem(PRESET_EDITOR_SNAP_STORAGE_KEY, String(isSnapEnabled));
    } catch {
      // Ignore localStorage write failures.
    }
  }, [isSnapEnabled]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      window.localStorage.setItem(PRESET_EDITOR_SNAP_GRID_TYPE_STORAGE_KEY, snapGridType);
    } catch {
      // Ignore localStorage write failures.
    }
  }, [snapGridType]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      window.localStorage.setItem(PRESET_EDITOR_SNAP_GRID_VISIBLE_STORAGE_KEY, String(isSnapGridVisible));
    } catch {
      // Ignore localStorage write failures.
    }
  }, [isSnapGridVisible]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      window.localStorage.setItem(PRESET_EDITOR_SNAP_MODE_STORAGE_KEY, presetSnapMode);
    } catch {
      // Ignore localStorage write failures.
    }
  }, [presetSnapMode]);

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    const presetChangedWhileOpen = isOpen && previousPresetIdRef.current !== selectedPresetId;
    if (justOpened || presetChangedWhileOpen) {
      if (justOpened) setPreviewZoomPercent(PRESET_PREVIEW_ZOOM_DEFAULT);
      setSelectionKeys([]);
      setDragState(null);
      moveAxisLockRef.current = null;
      setIsHoveringSelectable(false);
      setSnapGuides({ vertical: [], horizontal: [] });
      setDraggingLayerId(null);
      setActiveLayerPanelId(null);
      setBindingDropPreview(null);
      setIsPrimitiveAddMenuOpen(false);
      setIsTextAddMenuOpen(false);
      setIsPresetSnapModeMenuOpen(false);
      autoMaterializedPresetRef.current = null;
    }
    wasOpenRef.current = isOpen;
    previousPresetIdRef.current = selectedPresetId;
  }, [allSelectionKeys, isOpen, selectedPresetId]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectionKeys((prev) => sanitizeSelectionKeys(prev, allSelectionKeys));
  }, [isOpen, selectionSignature, allSelectionKeys]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectionKeys((prev) => prev.filter((key) => {
      const layerId = selectionKeyToLayerId(key);
      return Boolean(layerId && isLayerVisible(layerId) && !isLayerLocked(layerId));
    }));
  }, [isLayerLocked, isLayerVisible, isOpen, selectionKeyToLayerId]);

  useEffect(() => {
    setMultiSelectionRotationDeg(0);
  }, [selectionKeySignature]);

  useEffect(() => {
    if (!isSnapEnabled) setSnapGuides({ vertical: [], horizontal: [] });
  }, [isSnapEnabled]);

  useEffect(() => {
    if (!dragState) setBindingDropPreview(null);
  }, [dragState]);

  useEffect(() => {
    if (!isOpen || !draftPreset) return;
    const autoMaterializeKey = `${selectedPresetId}|${draftPreset.preset}`;
    if (autoMaterializedPresetRef.current === autoMaterializeKey) return;
    const baseTemplateStillVisible = (
      draftPreset.templateShapeVisible !== false
      || draftPreset.templateMainTextVisible !== false
      || draftPreset.templateSubTextVisible !== false
    );
    if (!baseTemplateStillVisible) return;

    const patch = buildTemplateMaterializationPatch(draftPreset, geometry?.templateBlueprint);
    if (!patch || !Array.isArray(patch.shapePrimitives)) return;

    autoMaterializedPresetRef.current = autoMaterializeKey;
    onDraftChange(patch, {
      trackUndo: false,
      markDirty: false,
      resetBaseline: true,
    });
    setSelectionKeys((prev) => (prev.includes(TEMPLATE_KEY)
      ? prev.filter((key) => key !== TEMPLATE_KEY)
      : prev));
    setActiveLayerPanelId((prev) => (prev && TEMPLATE_BASE_LAYER_IDS.has(prev) ? null : prev));
  }, [buildTemplateMaterializationPatch, draftPreset, geometry?.templateBlueprint, isOpen, onDraftChange, selectedPresetId]);

  useEffect(() => {
    if (!isOpen) return;
    if (singleSelectionLayerId) {
      setActiveLayerPanelId(singleSelectionLayerId);
    }
  }, [isOpen, singleSelectionLayerId]);

  useEffect(() => {
    if (!activeLayerPanelId) return;
    if (!layerPanelOrder.includes(activeLayerPanelId)) {
      setActiveLayerPanelId(null);
    }
  }, [activeLayerPanelId, layerPanelOrder]);

  const toCanvasPoint = useCallback((event) => {
    const canvas = previewCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return {
      x: ((event.clientX - rect.left) / rect.width) * canvasWidth,
      y: ((event.clientY - rect.top) / rect.height) * canvasHeight,
    };
  }, [canvasHeight, canvasWidth]);

  const resolveHitKey = useCallback((localX, localY) => {
    if (!draftPreset) return null;
    for (let index = layerOrder.length - 1; index >= 0; index -= 1) {
      const layerId = layerOrder[index];
      if (TEMPLATE_BASE_LAYER_IDS.has(layerId)) continue;
      if (!isLayerVisible(layerId) || isLayerLocked(layerId)) continue;


      const primitiveId = layerIdToPrimitiveId(layerId);
      if (primitiveId) {
        const item = geometry.primitives.find((shape) => shape?.id === primitiveId);
        if (item?.id && pointInBounds(localX, localY, item.bounds)) return primitiveSelectionKey(item.id);
        continue;
      }
    }
    return null;
  }, [draftPreset, geometry, isLayerLocked, isLayerVisible, layerOrder]);

  const getAxisLockedMoveTranslate = useCallback((rawTranslate, shiftKey) => {
    const safeTranslate = {
      x: Number(rawTranslate?.x) || 0,
      y: Number(rawTranslate?.y) || 0,
    };
    if (!shiftKey) {
      moveAxisLockRef.current = null;
      return { delta: safeTranslate, axisLock: null };
    }

    if (!moveAxisLockRef.current) {
      if (Math.hypot(safeTranslate.x, safeTranslate.y) < MOVE_AXIS_LOCK_THRESHOLD) {
        return { delta: safeTranslate, axisLock: null };
      }
      moveAxisLockRef.current = Math.abs(safeTranslate.x) >= Math.abs(safeTranslate.y) ? 'x' : 'y';
    }

    if (moveAxisLockRef.current === 'x') {
      return {
        delta: { x: safeTranslate.x, y: 0 },
        axisLock: 'x',
      };
    }
    if (moveAxisLockRef.current === 'y') {
      return {
        delta: { x: 0, y: safeTranslate.y },
        axisLock: 'y',
      };
    }
    return { delta: safeTranslate, axisLock: null };
  }, []);

  const resolveElementAxisSnaps = useCallback(({
    dragInfo,
    delta,
  }) => {
    if (!dragInfo?.startBoundsWorld || !dragInfo?.startGeometry) {
      return { x: null, y: null };
    }

    const movedBounds = offsetBoundsByDelta(dragInfo.startBoundsWorld, delta);
    if (!movedBounds) return { x: null, y: null };
    const movedAnchorsX = getBoundsAxisAnchors(movedBounds, 'x');
    const movedAnchorsY = getBoundsAxisAnchors(movedBounds, 'y');
    const selectedPrimitiveIdSet = new Set(
      (dragInfo.selectionKeys || [])
        .map((key) => selectionKeyToPrimitiveId(key))
        .filter(Boolean),
    );
    const worldOffsetX = Number(dragInfo.startConfig?.posX) || 0;
    const worldOffsetY = Number(dragInfo.startConfig?.posY) || 0;

    let bestX = null;
    let bestY = null;
    for (const primitive of (dragInfo.startGeometry?.primitives || [])) {
      const primitiveId = primitive?.id;
      if (!primitiveId || selectedPrimitiveIdSet.has(primitiveId)) continue;

      const candidateKey = primitiveSelectionKey(primitiveId);
      const candidateLayerId = selectionKeyToLayerId(candidateKey);
      if (!candidateLayerId || !isLayerVisible(candidateLayerId) || isLayerLocked(candidateLayerId)) continue;

      const candidateBoundsLocal = primitive?.bounds || primitive?.localBounds;
      const candidateBounds = buildWorldBoundsFromLocalBounds(candidateBoundsLocal, worldOffsetX, worldOffsetY);
      if (!candidateBounds) continue;

      const orthogonalGapForX = intervalGap(
        movedBounds.minY,
        movedBounds.maxY,
        candidateBounds.minY,
        candidateBounds.maxY,
      );
      if (orthogonalGapForX <= PRESET_ELEMENT_SNAP_PROXIMITY) {
        const candidateLinesX = getBoundsAxisAnchors(candidateBounds, 'x');
        for (const anchor of movedAnchorsX) {
          for (const line of candidateLinesX) {
            if (anchor.kind !== line.kind) continue;
            const correction = line.value - anchor.value;
            const distance = Math.abs(correction);
            if (distance > SNAP_THRESHOLD) continue;
            bestX = choosePreferredElementAxisSnap(bestX, {
              source: 'element',
              primitiveId,
              line: line.value,
              lineKey: line.key,
              lineKind: line.kind,
              correction,
              distance,
              anchorKey: anchor.key,
              anchorKind: anchor.kind,
            });
          }
        }
      }

      const orthogonalGapForY = intervalGap(
        movedBounds.minX,
        movedBounds.maxX,
        candidateBounds.minX,
        candidateBounds.maxX,
      );
      if (orthogonalGapForY <= PRESET_ELEMENT_SNAP_PROXIMITY) {
        const candidateLinesY = getBoundsAxisAnchors(candidateBounds, 'y');
        for (const anchor of movedAnchorsY) {
          for (const line of candidateLinesY) {
            if (anchor.kind !== line.kind) continue;
            const correction = line.value - anchor.value;
            const distance = Math.abs(correction);
            if (distance > SNAP_THRESHOLD) continue;
            bestY = choosePreferredElementAxisSnap(bestY, {
              source: 'element',
              primitiveId,
              line: line.value,
              lineKey: line.key,
              lineKind: line.kind,
              correction,
              distance,
              anchorKey: anchor.key,
              anchorKind: anchor.kind,
            });
          }
        }
      }
    }

    return { x: bestX, y: bestY };
  }, [isLayerLocked, isLayerVisible, selectionKeyToLayerId]);

  const updateDragFromPoint = useCallback((point, dragInfo, inputState = null) => {
    if (!point || !dragInfo || typeof getPresetEditorGeometry !== 'function') return;
    const isShiftHeld = Boolean(inputState?.shiftKey);

    const rawTranslate = {
      x: point.x - dragInfo.startPoint.x,
      y: point.y - dragInfo.startPoint.y,
    };
    let nextTranslate = rawTranslate;
    let activeMoveAxisLock = null;
    if (dragInfo.mode === 'move') {
      const axisLocked = getAxisLockedMoveTranslate(rawTranslate, isShiftHeld);
      nextTranslate = axisLocked.delta;
      activeMoveAxisLock = axisLocked.axisLock;
    }
    if (dragInfo.mode === 'move' && dragInfo.snapEnabled && dragInfo.startBoundsWorld) {
      const gridSnapped = isPresetGridSnapActive
        ? snapTranslatedBounds({
          startBounds: dragInfo.startBoundsWorld,
          delta: nextTranslate,
          canvasWidth,
          canvasHeight,
          gridType: snapGridType,
        })
        : null;
      const elementSnapped = isPresetElementSnapActive
        ? resolveElementAxisSnaps({
          dragInfo,
          delta: nextTranslate,
        })
        : { x: null, y: null };
      let chosenXSnap = choosePreferredAxisSnap(
        gridSnapped?.snapMeta?.x ? { ...gridSnapped.snapMeta.x, source: 'grid' } : null,
        elementSnapped.x,
      );
      let chosenYSnap = choosePreferredAxisSnap(
        gridSnapped?.snapMeta?.y ? { ...gridSnapped.snapMeta.y, source: 'grid' } : null,
        elementSnapped.y,
      );
      if (activeMoveAxisLock === 'x') chosenYSnap = null;
      if (activeMoveAxisLock === 'y') chosenXSnap = null;

      nextTranslate = {
        x: nextTranslate.x + (chosenXSnap?.correction || 0),
        y: nextTranslate.y + (chosenYSnap?.correction || 0),
      };
      setSnapGuides({
        vertical: chosenXSnap ? [clamp(chosenXSnap.line, 0, canvasWidth)] : [],
        horizontal: chosenYSnap ? [clamp(chosenYSnap.line, 0, canvasHeight)] : [],
      });
    } else if (dragInfo.mode === 'move') {
      setSnapGuides({ vertical: [], horizontal: [] });
    } else if (dragInfo.mode !== 'move') {
      setSnapGuides({ vertical: [], horizontal: [] });
    }

    let affine = {
      center: dragInfo.selectionCenter,
      scale: 1,
      rotateDeg: 0,
      translate: nextTranslate,
    };

    if (dragInfo.mode === 'scale') {
      const vector = {
        x: point.x - dragInfo.selectionCenter.x,
        y: point.y - dragInfo.selectionCenter.y,
      };
      if (dragInfo.scaleMode === 'x' || dragInfo.scaleMode === 'y') {
        const projected = projectVectorToAxes(vector, dragInfo.scaleAxesRotateDeg || 0);
        const axisDistance = dragInfo.scaleMode === 'x'
          ? Math.max(1, Number(dragInfo.startAxisDistanceX) || 0)
          : Math.max(1, Number(dragInfo.startAxisDistanceY) || 0);
        const currentAxisDistance = Math.abs(
          dragInfo.scaleMode === 'x'
            ? projected.x
            : projected.y,
        );
        const axisRatio = clamp(currentAxisDistance / axisDistance, 0.15, 8);
        affine = {
          center: dragInfo.selectionCenter,
          scale: 1,
          scaleX: dragInfo.scaleMode === 'x' ? axisRatio : 1,
          scaleY: dragInfo.scaleMode === 'y' ? axisRatio : 1,
          scaleMode: dragInfo.scaleMode,
          scaleAxesRotateDeg: Number(dragInfo.scaleAxesRotateDeg || 0),
          rotateDeg: 0,
          translate: { x: 0, y: 0 },
        };
      } else {
        const currentDistance = Math.hypot(vector.x, vector.y);
        const ratio = dragInfo.startDistance > 0 ? currentDistance / dragInfo.startDistance : 1;
        affine = {
          center: dragInfo.selectionCenter,
          scale: clamp(ratio, 0.15, 8),
          scaleMode: 'uniform',
          rotateDeg: 0,
          translate: { x: 0, y: 0 },
        };
      }
    } else if (dragInfo.mode === 'rotate') {
      const vector = {
        x: point.x - dragInfo.selectionCenter.x,
        y: point.y - dragInfo.selectionCenter.y,
      };
      const currentAngle = Math.atan2(vector.y, vector.x);
      const rotateDelta = normalizeDragRotationDelta(currentAngle - dragInfo.startAngle);
      affine = {
        center: dragInfo.selectionCenter,
        scale: 1,
        rotateDeg: rotateDelta,
        translate: { x: 0, y: 0 },
      };
      setDragState((prev) => {
        if (!prev || prev.mode !== 'rotate') return prev;
        return {
          ...prev,
          previewRotateDeg: normalizeAngle((dragInfo.frameBaseAngle || 0) + rotateDelta),
        };
      });
    }

    let patch = null;
    if (dragInfo.mode === 'scale' && dragInfo.boundResize && dragInfo.handleId) {
      patch = buildBoundResizePatchForScaleDrag({ point, dragInfo });
    }
    if (!patch) {
      patch = applySelectionAffineTransform({
        startConfig: dragInfo.startConfig,
        startGeometry: dragInfo.startGeometry,
        selectionKeys: dragInfo.selectionKeys,
        affine,
        getGeometryForConfig: getPresetEditorGeometry,
        canvasWidth,
        canvasHeight,
      });
    }

    let candidatePreset = null;
    let candidateGeometry = null;
    if (Object.keys(patch).length > 0) {
      candidatePreset = {
        ...dragInfo.startConfig,
        ...patch,
      };

      if (Array.isArray(patch.shapePrimitives)) {
        const selectedPrimitiveIds = (dragInfo.selectionKeys || [])
          .map((key) => selectionKeyToPrimitiveId(key))
          .filter(Boolean);
        const selectedPrimitiveIdSet = new Set(selectedPrimitiveIds);
        const directBoundTargetIds = selectedPrimitiveIds.filter((primitiveId) => {
          const candidateShape = findPrimitiveById(candidatePreset.shapePrimitives, primitiveId);
          if (!candidateShape || !isAutoSizeBindingTarget(candidateShape)) return false;
          const binding = sanitizeAutoSizeBinding(candidateShape.autoSizeBinding, candidateShape);
          if (!binding) return false;
          const sourceIds = getAutoSizeBindingSourceIds(binding);
          return !sourceIds.some((sourceId) => selectedPrimitiveIdSet.has(sourceId));
        });

        if (directBoundTargetIds.length > 0) {
          clearBindingTextMoveBaselineEntriesByShapeIds(directBoundTargetIds);
          const recalibrated = recalibrateBoundShapeIds({
            preset: candidatePreset,
            shapePrimitives: candidatePreset.shapePrimitives,
            targetPrimitiveIds: directBoundTargetIds,
          });
          if (recalibrated.shapePrimitives && recalibrated.shapePrimitives !== candidatePreset.shapePrimitives) {
            patch = { ...patch, shapePrimitives: recalibrated.shapePrimitives };
            candidatePreset = {
              ...candidatePreset,
              shapePrimitives: recalibrated.shapePrimitives,
            };
          }
          candidateGeometry = recalibrated.geometry || null;
        }
      }
    }

    if (dragInfo.mode === 'move' || dragInfo.mode === 'scale') {
      if (!candidatePreset && Object.keys(patch).length === 0) {
        candidatePreset = dragInfo.startConfig;
      }
      if (candidatePreset && !candidateGeometry) {
        candidateGeometry = getPresetEditorGeometry(candidatePreset) || null;
      }
      if (candidatePreset && candidateGeometry && Array.isArray(candidatePreset.shapePrimitives)) {
        const reshaped = reshapeBindingsForMovedTextSelection({
          dragInfo,
          candidatePreset,
          candidateGeometry,
        });
        if (reshaped?.anchorsChanged && reshaped.anchorsByTextId) {
          setDragState((prev) => {
            if (!prev || prev.mode !== 'move') return prev;
            if (prev.undoGroup !== dragInfo.undoGroup) return prev;
            if (prev.bindingTextMoveAnchors === reshaped.anchorsByTextId) return prev;
            bindingTextMoveAnchorMemoryRef.current = {
              ...(bindingTextMoveAnchorMemoryRef.current || {}),
              ...reshaped.anchorsByTextId,
            };
            return {
              ...prev,
              bindingTextMoveAnchors: reshaped.anchorsByTextId,
            };
          });
        }
        if (reshaped?.shapePrimitives && reshaped.shapePrimitives !== candidatePreset.shapePrimitives) {
          patch = { ...patch, shapePrimitives: reshaped.shapePrimitives };
          candidatePreset = {
            ...candidatePreset,
            shapePrimitives: reshaped.shapePrimitives,
          };
          candidateGeometry = getPresetEditorGeometry(candidatePreset) || candidateGeometry;
        }
      }
      if (dragInfo.mode === 'move') {
        const preview = buildBindingDropPreviewForMove({
          point,
          dragInfo,
          candidatePreset,
          candidateGeometry,
        });
        setBindingDropPreview(preview);
      } else {
        setBindingDropPreview(null);
      }
    } else {
      setBindingDropPreview(null);
    }

    if (Object.keys(patch).length > 0) {
      onDraftChange(
        patch,
        dragInfo.undoGroup ? { undoGroup: dragInfo.undoGroup } : undefined,
      );
    }
  }, [
    buildBoundResizePatchForScaleDrag,
    buildBindingDropPreviewForMove,
    canvasHeight,
    canvasWidth,
    clearBindingTextMoveBaselineEntriesByShapeIds,
    getPresetEditorGeometry,
    getAxisLockedMoveTranslate,
    isPresetElementSnapActive,
    isPresetGridSnapActive,
    onDraftChange,
    recalibrateBoundShapeIds,
    resolveElementAxisSnaps,
    reshapeBindingsForMovedTextSelection,
    snapGridType,
  ]);

  const updateMarqueeFromPoint = useCallback((point, dragInfo) => {
    if (!point || !dragInfo || !draftPreset) return;

    const dx = point.x - dragInfo.startPoint.x;
    const dy = point.y - dragInfo.startPoint.y;
    const crossedThreshold = Math.hypot(dx, dy) >= MARQUEE_DRAG_THRESHOLD;

    let nextSelection = dragInfo.baseSelection;
    if (crossedThreshold) {
      const marqueeBoundsWorld = normalizeMarqueeBounds(dragInfo.startPoint, point);
      if (marqueeBoundsWorld) {
        const marqueeBoundsLocal = {
          minX: marqueeBoundsWorld.minX - draftPreset.posX,
          minY: marqueeBoundsWorld.minY - draftPreset.posY,
          maxX: marqueeBoundsWorld.maxX - draftPreset.posX,
          maxY: marqueeBoundsWorld.maxY - draftPreset.posY,
          width: marqueeBoundsWorld.width,
          height: marqueeBoundsWorld.height,
        };
        const hitKeys = getSelectionKeysIntersectingBounds(geometry, marqueeBoundsLocal);
        const selectableHitKeys = hitKeys.filter((key) => {
          const layerId = selectionKeyToLayerId(key);
          return Boolean(layerId && isLayerVisible(layerId) && !isLayerLocked(layerId));
        });
        nextSelection = dragInfo.additive
          ? sanitizeSelectionKeys([...dragInfo.baseSelection, ...selectableHitKeys], allSelectionKeys)
          : selectableHitKeys;
      }
    }

    setDragState((prev) => {
      if (!prev || prev.mode !== 'marquee') return prev;
      return {
        ...prev,
        currentPoint: point,
        crossedThreshold,
      };
    });
    setSelectionKeys(nextSelection);
  }, [allSelectionKeys, draftPreset, geometry, isLayerLocked, isLayerVisible, selectionKeyToLayerId]);

  useEffect(() => {
    if (!dragState) return undefined;
    const handleMove = (event) => {
      event.preventDefault();
      const point = toCanvasPoint(event);
      if (!point) return;
      if (dragState.mode === 'marquee') {
        updateMarqueeFromPoint(point, dragState);
        return;
      }
      updateDragFromPoint(point, dragState, { shiftKey: event.shiftKey });
    };
    const handleUp = () => {
      if (dragState.mode === 'move') {
        if (dragState.bindingTextMoveAnchors && typeof dragState.bindingTextMoveAnchors === 'object') {
          bindingTextMoveAnchorMemoryRef.current = {
            ...(bindingTextMoveAnchorMemoryRef.current || {}),
            ...dragState.bindingTextMoveAnchors,
          };
        }
        const preview = bindingDropPreviewRef.current;
        if (preview?.commitPatch) {
          if (preview.sourceTextId && bindingTextMoveAnchorMemoryRef.current?.[preview.sourceTextId]) {
            const nextMemory = { ...(bindingTextMoveAnchorMemoryRef.current || {}) };
            delete nextMemory[preview.sourceTextId];
            bindingTextMoveAnchorMemoryRef.current = nextMemory;
          }
          if (preview.targetShapeId) clearBindingTextMoveBaselineEntriesByShapeIds([preview.targetShapeId]);
          if (preview.sourceTextId) clearBindingTextMoveBaselineEntriesBySourceTextId(preview.sourceTextId);
          onDraftChange(
            preview.commitPatch,
            dragState.undoGroup ? { undoGroup: dragState.undoGroup } : undefined,
          );
        }
      }
      if (dragState.mode === 'marquee' && !dragState.crossedThreshold) {
        setSelectionKeys(dragState.additive ? dragState.baseSelection : []);
      }
      if (dragState.mode === 'rotate' && dragState.selectionKeys?.length > 1) {
        setMultiSelectionRotationDeg(Number(dragState.previewRotateDeg || 0));
      }
      moveAxisLockRef.current = null;
      setDragState(null);
      setBindingDropPreview(null);
      setIsHoveringSelectable(false);
      setSnapGuides({ vertical: [], horizontal: [] });
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('mouseleave', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('mouseleave', handleUp);
    };
  }, [
    dragState,
    onDraftChange,
    toCanvasPoint,
    updateDragFromPoint,
    updateMarqueeFromPoint,
    clearBindingTextMoveBaselineEntriesByShapeIds,
    clearBindingTextMoveBaselineEntriesBySourceTextId,
  ]);

  useEffect(() => {
    if (!isOpen || !draftPreset) return;
    const canvas = previewCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    drawPreviewFrame(ctx, draftPreset);

    const hideAxisAlignedOverlay = dragState?.mode === 'rotate' || hasRotatedFrame;
    if (hideAxisAlignedOverlay) return;

    const selectedLocalBounds = selectionKeys
      .map((key) => getBoundsForSelectionKey(geometry, key))
      .filter(Boolean);
    if (!selectedLocalBounds.length) return;

    ctx.save();
    ctx.setLineDash([10, 8]);
    ctx.lineWidth = 2;
    ctx.strokeStyle = 'rgba(147, 197, 253, 0.95)';
    for (const bounds of selectedLocalBounds) {
      ctx.strokeRect(
        bounds.minX + draftPreset.posX,
        bounds.minY + draftPreset.posY,
        Math.max(1, bounds.width),
        Math.max(1, bounds.height),
      );
    }
    ctx.restore();

    if (!selectedBoundsForCanvasOverlay) return;
    ctx.save();
    ctx.setLineDash([18, 10]);
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#60a5fa';
    ctx.strokeRect(
      selectedBoundsForCanvasOverlay.minX,
      selectedBoundsForCanvasOverlay.minY,
      Math.max(1, selectedBoundsForCanvasOverlay.width),
      Math.max(1, selectedBoundsForCanvasOverlay.height),
    );
    ctx.fillStyle = 'rgba(96, 165, 250, 0.17)';
    ctx.fillRect(
      selectedBoundsForCanvasOverlay.minX,
      selectedBoundsForCanvasOverlay.minY,
      Math.max(1, selectedBoundsForCanvasOverlay.width),
      Math.max(1, selectedBoundsForCanvasOverlay.height),
    );
    ctx.restore();
  }, [canvasHeight, canvasWidth, draftPreset, dragState, drawPreviewFrame, geometry, hasRotatedFrame, isOpen, selectedBoundsForCanvasOverlay, selectionKeys]);

  if (!isOpen || !draftPreset) return null;

  const handleField = (field) => (event) => onDraftChange({ [field]: event.target.value });
  const handlePresetFamilyChange = (event) => {
    const nextPresetFamily = event.target.value;
    if (!nextPresetFamily || nextPresetFamily === draftPreset.preset) return;
    const candidatePreset = {
      ...cloneDraftPreset(draftPreset),
      preset: nextPresetFamily,
    };
    const candidateGeometry = typeof getPresetEditorGeometry === 'function'
      ? getPresetEditorGeometry(candidatePreset)
      : null;
    const materializePatch = buildTemplateMaterializationPatch(candidatePreset, candidateGeometry?.templateBlueprint);
    onDraftChange({
      preset: nextPresetFamily,
      ...(materializePatch || {}),
    });
    setSelectionKeys([]);
    setActiveLayerPanelId(null);
  };

  const handleCanvasMouseMove = (event) => {
    if (dragState) return;
    const point = toCanvasPoint(event);
    if (!point) {
      setIsHoveringSelectable(false);
      return;
    }
    const localX = point.x - draftPreset.posX;
    const localY = point.y - draftPreset.posY;
    setIsHoveringSelectable(Boolean(resolveHitKey(localX, localY)));
  };

  const handleCanvasMouseLeave = () => {
    if (dragState) return;
    setIsHoveringSelectable(false);
  };

  const handleCanvasMouseDown = (event) => {
    if (isRecording || typeof getPresetEditorGeometry !== 'function') return;
    setIsHoveringSelectable(false);
    setSnapGuides({ vertical: [], horizontal: [] });
    setBindingDropPreview(null);
    moveAxisLockRef.current = null;
    const point = toCanvasPoint(event);
    if (!point) return;

    const localX = point.x - draftPreset.posX;
    const localY = point.y - draftPreset.posY;
    const hitKey = resolveHitKey(localX, localY);

    if (!hitKey) {
      const baseSelection = event.shiftKey
        ? sanitizeSelectionKeys(selectionKeys, allSelectionKeys)
        : [];
      if (!event.shiftKey) setSelectionKeys([]);
      setDragState({
        mode: 'marquee',
        startPoint: point,
        currentPoint: point,
        baseSelection,
        additive: event.shiftKey,
        crossedThreshold: false,
      });
      return;
    }

    const selectionOutcome = resolveSelectionFromHit({
      currentSelection: selectionKeys,
      hitKey,
      shiftKey: event.shiftKey,
      allKeys: allSelectionKeys,
    });
    let nextSelectionForState = selectionOutcome.nextSelection;
    let dragSelection = selectionOutcome.dragSelection;
    let autoIncludedBindingSourceTextIds = [];
    if (selectionOutcome.shouldStartDrag && selectionOutcome.dragSelection?.length) {
      const explicitSelectionKeySet = new Set(selectionOutcome.dragSelection);
      dragSelection = expandMoveDragSelectionWithLinkedTextSources(selectionOutcome.dragSelection);
      autoIncludedBindingSourceTextIds = dragSelection
        .filter((key) => !explicitSelectionKeySet.has(key))
        .map((key) => selectionKeyToPrimitiveId(key))
        .filter(Boolean);
    }
    setSelectionKeys(nextSelectionForState);

    if (!selectionOutcome.shouldStartDrag || !dragSelection?.length) return;
    const dragLayerIds = dragSelection.map((key) => selectionKeyToLayerId(key)).filter(Boolean);
    if (dragLayerIds.some((layerId) => isLayerLocked(layerId) || !isLayerVisible(layerId))) return;

    const startConfig = cloneDraftPreset(draftPreset);
    const startGeometry = getPresetEditorGeometry(startConfig);
    const startBoundsWorld = getSelectionBoundsWorld(
      startGeometry,
      startConfig,
      dragSelection,
    );
    const seedBindingTextMoveAnchors = dragSelection.reduce((acc, key) => {
      const primitiveId = selectionKeyToPrimitiveId(key);
      if (!primitiveId) return acc;
      const cached = bindingTextMoveAnchorMemoryRef.current?.[primitiveId];
      if (!cached || (cached.x == null && cached.y == null)) return acc;
      acc[primitiveId] = { ...cached };
      return acc;
    }, {});
    const selectionCenter = startBoundsWorld ? boundsCenter(startBoundsWorld) : point;
    const nextDrag = {
      mode: 'move',
      undoGroup: createDragUndoGroup('move'),
      startPoint: point,
      startConfig,
      startGeometry,
      selectionKeys: dragSelection,
      selectionCenter,
      startBoundsWorld,
      startDistance: 0,
      startAngle: 0,
      snapEnabled: isSnapEnabled,
      bindingTextMoveAnchors: Object.keys(seedBindingTextMoveAnchors).length
        ? seedBindingTextMoveAnchors
        : null,
      autoIncludedBindingSourceTextIds,
    };
    moveAxisLockRef.current = null;
    setDragState(nextDrag);
    updateDragFromPoint(point, nextDrag, { shiftKey: event.shiftKey });
  };

  const startHandleDrag = (handle) => (event) => {
    if (isRecording || typeof getPresetEditorGeometry !== 'function') return;
    event.preventDefault();
    event.stopPropagation();

    const mode = handle?.mode;
    if (!mode) return;
    const scaleMode = handle?.scaleMode || 'uniform';
    const handleId = handle?.handleId || null;
    const point = toCanvasPoint(event);
    if (!point) return;

    const activeSelection = sanitizeSelectionKeys(selectionKeys, allSelectionKeys);
    if (!activeSelection.length) return;
    const activeLayerIds = activeSelection.map((key) => selectionKeyToLayerId(key)).filter(Boolean);
    if (activeLayerIds.some((layerId) => isLayerLocked(layerId) || !isLayerVisible(layerId))) return;

    const startConfig = cloneDraftPreset(draftPreset);
    const startGeometry = getPresetEditorGeometry(startConfig);
    const startBoundsWorld = getSelectionBoundsWorld(startGeometry, startConfig, activeSelection);
    if (!startBoundsWorld) return;

    const selectionCenter = boundsCenter(startBoundsWorld);
    const vector = {
      x: point.x - selectionCenter.x,
      y: point.y - selectionCenter.y,
    };
    const scaleAxesRotateDeg = mode === 'scale' ? Number(frameRotateDeg || 0) : 0;
    const projectedScaleVector = projectVectorToAxes(vector, scaleAxesRotateDeg);
    const boundResize = mode === 'scale'
      ? resolveBoundResizeGestureForScaleDrag({
        startConfig,
        startGeometry,
        selectionKeys: activeSelection,
        handleId,
      })
      : null;
    const baseRotation = mode === 'rotate'
      ? (activeSelection.length > 1 ? multiSelectionRotationDeg : singleSelectionRotationDeg)
      : 0;
    const nextDrag = {
      mode,
      undoGroup: createDragUndoGroup(mode),
      scaleMode,
      startPoint: point,
      startConfig,
      startGeometry,
      selectionKeys: activeSelection,
      selectionCenter,
      startBoundsWorld,
      startDistance: Math.hypot(vector.x, vector.y),
      startAxisDistanceX: Math.abs(projectedScaleVector.x),
      startAxisDistanceY: Math.abs(projectedScaleVector.y),
      handleId,
      boundResize,
      scaleAxesRotateDeg,
      startAngle: Math.atan2(vector.y, vector.x),
      previewRotateDeg: baseRotation,
      frameBaseAngle: baseRotation,
    };

    if (mode === 'scale') {
      if (scaleMode === 'x' && nextDrag.startAxisDistanceX < 1) return;
      if (scaleMode === 'y' && nextDrag.startAxisDistanceY < 1) return;
      if (scaleMode === 'uniform' && nextDrag.startDistance < 1) return;
    }

    moveAxisLockRef.current = null;
    setDragState(nextDrag);
    setSnapGuides({ vertical: [], horizontal: [] });
    setBindingDropPreview(null);
    updateDragFromPoint(point, nextDrag, { shiftKey: event.shiftKey });
  };

  const updatePrimitive = (shapeId, updater) => {
    const baseNextShapePrimitives = shapePrimitives.map((shape, index) => {
      if (primitiveDraftId(shape, index) !== shapeId) return shape;
      const updated = { ...shape, ...updater };
      updated.layerKind = updated.layerKind === 'text'
        ? 'text'
        : (updated.layerKind === 'image' || updated.type === 'image' ? 'image' : 'shape');
      updated.textSource = normalizeTextSource(updated.textSource, 'main');
      if (updated.type === 'circle') {
        const size = clamp(Number(updated.width ?? updated.height ?? 90), 8, 1200);
        updated.width = size;
        updated.height = size;
      } else {
        updated.width = clamp(Number(updated.width ?? 220), 8, 1920);
        updated.height = clamp(Number(updated.height ?? 80), updated.type === 'line' ? 1 : 8, 1080);
      }
      updated.rotation = clamp(Number(updated.rotation ?? 0), -180, 180);
      updated.strokeWidth = clamp(Number(updated.strokeWidth ?? 3), 1, 80);
      updated.skewX = clamp(Number(updated.skewX ?? 0), -1920, 1920);
      updated.cornerRadius = updated.type === 'roundedRect'
        ? clamp(Number(updated.cornerRadius ?? 16), 0, 540)
        : 0;
      updated.fill = updated.layerKind === 'text'
        ? false
        : updated.layerKind === 'image'
          ? false
          : (updated.type === 'line' ? false : updated.fill !== false);
      updated.stroke = updated.layerKind === 'text'
        ? false
        : updated.layerKind === 'image'
          ? false
          : (updated.type === 'line' ? true : Boolean(updated.stroke));
      updated.colorRole = normalizePrimitiveColorRole(updated.colorRole, 'primary');
      updated.fillColorMode = normalizePrimitiveColorMode(updated.fillColorMode, 'theme');
      updated.fillColorRole = normalizePrimitiveColorRole(updated.fillColorRole, updated.colorRole);
      updated.fillColorValue = normalizePrimitiveColorValue(updated.fillColorValue, '#ffffff');
      updated.strokeColorMode = normalizePrimitiveColorMode(updated.strokeColorMode, 'theme');
      updated.strokeColorRole = normalizePrimitiveColorRole(updated.strokeColorRole, updated.colorRole);
      updated.strokeColorValue = normalizePrimitiveColorValue(updated.strokeColorValue, '#ffffff');
      updated.visible = updated.visible !== false;
      updated.mainTextVisible = Boolean(updated.mainTextVisible);
      updated.subTextVisible = Boolean(updated.subTextVisible);
      updated.textPaddingX = clamp(Number(updated.textPaddingX ?? 0), 0, 1920);
      updated.textFontRatio = clamp(Number(updated.textFontRatio ?? (updated.textSource === 'main' ? 0.82 : 0.76)), 0.2, 1.5);
      updated.textBaselineRatio = clamp(Number(updated.textBaselineRatio ?? 0.78), 0.2, 1.2);
      updated.textFontFamily = updated.layerKind === 'text'
        ? sanitizeTextLayerFontFamily(updated.textFontFamily)
        : null;
      updated.textValue = typeof updated.textValue === 'string' ? updated.textValue : '';
      updated.hideFromPeoplePanel = Boolean(updated.hideFromPeoplePanel);
      updated.autoSizeBinding = sanitizeAutoSizeBinding(updated.autoSizeBinding, updated);
      return updated;
    });

    let nextShapePrimitives = baseNextShapePrimitives;
    const updatedShape = findPrimitiveById(nextShapePrimitives, shapeId);
    if (draftPreset && updatedShape && isAutoSizeBindingTarget(updatedShape) && sanitizeAutoSizeBinding(updatedShape.autoSizeBinding, updatedShape)) {
      clearBindingTextMoveBaselineEntriesByShapeIds([shapeId]);
      const recalibrated = recalibrateBoundShapeIds({
        preset: draftPreset,
        shapePrimitives: nextShapePrimitives,
        targetPrimitiveIds: [shapeId],
      });
      nextShapePrimitives = recalibrated.shapePrimitives || nextShapePrimitives;
    }

    onDraftChange({
      shapePrimitives: nextShapePrimitives,
    });
  };

  const handleSelectedPrimitiveNumberChange = (field) => (event) => {
    if (!selectedPrimitiveId || !selectedPrimitive) return;
    const nextValue = Number(event.target.value || 0);
    if (selectedPrimitive.type === 'circle' && (field === 'width' || field === 'height')) {
      updatePrimitive(selectedPrimitiveId, {
        width: nextValue,
        height: nextValue,
      });
      return;
    }
    updatePrimitive(selectedPrimitiveId, { [field]: nextValue });
  };
  const toggleTextFontPicker = () => {
    if (selectedPrimitiveControlsDisabled || !selectedPrimitiveIsTextLayer) return;
    setIsTextFontPickerOpen((current) => !current);
  };
  const selectTextFontFamily = (family) => {
    if (!selectedPrimitiveId) return;
    updatePrimitive(selectedPrimitiveId, { textFontFamily: family });
    setTextFontSearchQuery('');
    setIsTextFontPickerOpen(false);
    textFontPickerButtonRef.current?.focus();
  };

  const updateLayerLocks = (nextLocks) => {
    onDraftChange({
      layerLocks: normalizePresetLayerLocks(nextLocks, shapePrimitives),
    });
  };

  const setLayerLock = (layerId, locked) => {
    if (!layerId) return;
    const nextLocks = { ...layerLocks };
    if (locked) nextLocks[layerId] = true;
    else delete nextLocks[layerId];
    updateLayerLocks(nextLocks);
  };

  const reorderLayersByPanelIndices = (fromPanelIndex, toPanelIndex) => {
    if (fromPanelIndex === toPanelIndex) return;
    const nextLayerOrder = reorderLayerOrderByPanelIndices({
      layerOrder,
      shapePrimitives,
      fromPanelIndex,
      toPanelIndex,
    });
    onDraftChange({ layerOrder: nextLayerOrder });
  };

  const addPrimitive = (type) => {
    const nextShape = withPrimitiveResetBaseline(createPrimitiveDraft(type), { overwrite: true });
    const nextShapePrimitives = [...shapePrimitives, nextShape];
    const nextLayerOrder = [...normalizePresetLayerOrder(draftPreset.layerOrder, shapePrimitives), primitiveLayerId(nextShape.id)];
    onDraftChange({
      shapePrimitives: nextShapePrimitives,
      layerOrder: normalizePresetLayerOrder(nextLayerOrder, nextShapePrimitives),
      layerLocks: normalizePresetLayerLocks(draftPreset.layerLocks, nextShapePrimitives),
    });
    setActiveLayerPanelId(primitiveLayerId(nextShape.id));
    setSelectionKeys([primitiveSelectionKey(nextShape.id)]);
    setIsTextAddMenuOpen(false);
  };

  const addSelectedPrimitiveType = () => {
    addPrimitive(selectedPrimitiveAddOption.value);
    setIsPrimitiveAddMenuOpen(false);
  };

  const selectPrimitiveAddType = (type) => {
    setSelectedPrimitiveAddType(type);
    setIsPrimitiveAddMenuOpen(false);
    setIsTextAddMenuOpen(false);
  };

  const addTextLayer = (textSource) => {
    const normalizedTextSource = normalizeTextSource(textSource, 'main');
    const blueprintMatchSource = normalizedTextSource === 'generic' ? 'sub' : normalizedTextSource;
    const blueprintTextLayer = geometry?.templateBlueprint?.layers?.find(
      (layer) => layer?.layerKind === 'text' && normalizeTextSource(layer?.textSource, 'main') === blueprintMatchSource,
    );
    let nextShape = createTextPrimitiveDraft(normalizedTextSource, blueprintTextLayer || {});
    if (normalizedTextSource === 'generic') {
      let hasSubtitleSibling = false;
      const lowestSubtitleBottom = shapePrimitives.reduce((maxBottom, shape) => {
        if (shape?.layerKind !== 'text') return maxBottom;
        if (!isSubtitleStyleTextSource(shape?.textSource)) return maxBottom;
        hasSubtitleSibling = true;
        const primitiveBottom = Number(shape?.y || 0) + Number(shape?.height || 0);
        return Math.max(maxBottom, primitiveBottom);
      }, Number.NEGATIVE_INFINITY);
      if (hasSubtitleSibling) nextShape.y = lowestSubtitleBottom + 12;
    }
    nextShape = withPrimitiveResetBaseline(nextShape, { overwrite: true });
    const nextShapePrimitives = [...shapePrimitives, nextShape];
    const nextLayerOrder = [...normalizePresetLayerOrder(draftPreset.layerOrder, shapePrimitives), primitiveLayerId(nextShape.id)];
    onDraftChange({
      shapePrimitives: nextShapePrimitives,
      layerOrder: normalizePresetLayerOrder(nextLayerOrder, nextShapePrimitives),
      layerLocks: normalizePresetLayerLocks(draftPreset.layerLocks, nextShapePrimitives),
    });
    setActiveLayerPanelId(primitiveLayerId(nextShape.id));
    setSelectionKeys([primitiveSelectionKey(nextShape.id)]);
    setIsTextAddMenuOpen(false);
    setIsPrimitiveAddMenuOpen(false);
  };

  const addSelectedTextType = () => {
    addTextLayer(selectedTextAddOption.value);
  };

  const selectTextAddType = (textType) => {
    setSelectedTextAddType(normalizeTextSource(textType, 'main'));
    setIsTextAddMenuOpen(false);
    setIsPrimitiveAddMenuOpen(false);
  };

  const deletePrimitiveById = (primitiveId) => {
    deletePrimitivesByIds([primitiveId]);
  };

  const duplicatePrimitiveById = (primitiveId) => {
    const source = shapePrimitives.find((shape, index) => primitiveDraftId(shape, index) === primitiveId);
    if (!source) return;
    const duplicate = withPrimitiveResetBaseline({
      ...source,
      id: `shape-${Date.now()}-${Math.floor(Math.random() * 1000000)}`,
      x: Number(source.x || 0) + 20,
      y: Number(source.y || 0) + 20,
      visible: source.visible !== false,
    }, { overwrite: true });
    const nextShapePrimitives = [...shapePrimitives, duplicate];
    const sourceLayerId = primitiveLayerId(primitiveId);
    const duplicateLayer = primitiveLayerId(duplicate.id);
    const nextLayerOrderBase = [...normalizePresetLayerOrder(draftPreset.layerOrder, shapePrimitives)];
    const sourceLayerIndex = nextLayerOrderBase.indexOf(sourceLayerId);
    if (sourceLayerIndex >= 0) nextLayerOrderBase.splice(sourceLayerIndex + 1, 0, duplicateLayer);
    else nextLayerOrderBase.push(duplicateLayer);
    onDraftChange({
      shapePrimitives: nextShapePrimitives,
      layerOrder: normalizePresetLayerOrder(nextLayerOrderBase, nextShapePrimitives),
      layerLocks: normalizePresetLayerLocks(draftPreset.layerLocks, nextShapePrimitives),
    });
    setActiveLayerPanelId(duplicateLayer);
    setSelectionKeys([primitiveSelectionKey(duplicate.id)]);
  };

  const handleResetTransforms = () => {
    if (isRecording) return;
    const activeSelection = sanitizeSelectionKeys(selectionKeys, allSelectionKeys);
    if (!activeSelection.length) return;
    if (activeSelection.some((key) => {
      const layerId = selectionKeyToLayerId(key);
      return layerId && isLayerLocked(layerId);
    })) return;

    let patch = resetSelectionTransforms({
      startConfig: draftPreset,
      startGeometry: geometry,
      selectionKeys: activeSelection,
      getGeometryForConfig: getPresetEditorGeometry,
      canvasWidth,
      canvasHeight,
    });
    if (Object.keys(patch).length > 0 && Array.isArray(patch.shapePrimitives)) {
      const selectedPrimitiveIds = activeSelection
        .map((key) => selectionKeyToPrimitiveId(key))
        .filter(Boolean);
      if (selectedPrimitiveIds.length) {
        clearBindingTextMoveBaselineEntriesByShapeIds(selectedPrimitiveIds);
        const recalibrated = recalibrateBoundShapeIds({
          preset: draftPreset,
          shapePrimitives: patch.shapePrimitives,
          targetPrimitiveIds: selectedPrimitiveIds,
        });
        if (recalibrated.shapePrimitives && recalibrated.shapePrimitives !== patch.shapePrimitives) {
          patch = { ...patch, shapePrimitives: recalibrated.shapePrimitives };
        }
      }
    }
    if (Object.keys(patch).length > 0) onDraftChange(patch);
  };

  const toggleLayerVisibility = (layerId) => {
    if (!layerId) return;
    const primitiveId = layerIdToPrimitiveId(layerId);
    if (!primitiveId) return;
    const primitive = shapePrimitives.find((shape, index) => primitiveDraftId(shape, index) === primitiveId);
    if (!primitive) return;
    updatePrimitive(primitiveId, { visible: primitive.visible === false });
  };

  const unbindPrimitiveAutoSize = (primitiveId) => {
    if (!primitiveId) return;
    clearBindingTextMoveBaselineEntriesByShapeIds([primitiveId]);
    updatePrimitive(primitiveId, { autoSizeBinding: null });
  };
  const removePrimitiveAutoSizeSourceLink = (primitiveId, sourceTextId) => {
    if (!primitiveId || !sourceTextId) return;
    const primitive = findPrimitiveById(shapePrimitives, primitiveId);
    if (!primitive) return;
    const binding = sanitizeAutoSizeBinding(primitive.autoSizeBinding, primitive);
    if (!binding || !bindingReferencesSourceTextId(binding, sourceTextId)) return;
    clearBindingTextMoveBaselineEntriesByShapeIds([primitiveId]);
    const nextBinding = removeSourceTextIdFromBinding(binding, primitive, sourceTextId);
    updatePrimitive(primitiveId, { autoSizeBinding: nextBinding });
  };

  const selectLinkedShapesForText = (sourceTextId) => {
    if (!sourceTextId) return;
    const linkedKeys = shapePrimitives
      .map((shape, index) => {
        const binding = sanitizeAutoSizeBinding(shape?.autoSizeBinding, shape);
        if (!binding || !bindingReferencesSourceTextId(binding, sourceTextId)) return null;
        const primitiveId = primitiveDraftId(shape, index);
        const layerId = primitiveLayerId(primitiveId);
        if (isLayerLocked(layerId) || !isLayerVisible(layerId)) return null;
        return primitiveSelectionKey(primitiveId);
      })
      .filter(Boolean);
    const sourceLayerId = primitiveLayerId(sourceTextId);
    const includeSource = !isLayerLocked(sourceLayerId) && isLayerVisible(sourceLayerId);
    const nextSelection = sanitizeSelectionKeys(
      includeSource ? [primitiveSelectionKey(sourceTextId), ...linkedKeys] : linkedKeys,
      allSelectionKeys,
    );
    if (!nextSelection.length) return;
    setSelectionKeys(nextSelection);
    setActiveLayerPanelId(sourceLayerId);
  };

  const handleLayerRowActivate = (layerId, event) => {
    if (!layerId) return;
    setActiveLayerPanelId(layerId);
    const selectionKey = layerIdToSelectionKey(layerId);
    if (!selectionKey) {
      setSelectionKeys([]);
      return;
    }
    if (isLayerLocked(layerId) || !isLayerVisible(layerId)) {
      setSelectionKeys([]);
      return;
    }
    if (event?.shiftKey) {
      const next = resolveSelectionFromHit({
        currentSelection: selectionKeys,
        hitKey: selectionKey,
        shiftKey: true,
        allKeys: allSelectionKeys,
      });
      setSelectionKeys(next.nextSelection);
      return;
    }
    setSelectionKeys([selectionKey]);
  };

  const beginLayerRename = (row, event) => {
    if (!row?.primitiveId || row.locked || isRecording) return;
    event?.preventDefault();
    event?.stopPropagation();
    cancelLayerRenameRef.current = false;
    setEditingLayerId(row.id);
    setEditingLayerName(row.hasCustomLabel ? row.label : row.defaultLabel || row.label || '');
    setActiveLayerPanelId(row.id);
  };

  const cancelLayerRename = () => {
    setEditingLayerId(null);
    setEditingLayerName('');
  };

  const commitLayerRename = () => {
    if (!editingLayerId) return;
    const primitiveId = layerIdToPrimitiveId(editingLayerId);
    if (!primitiveId) {
      cancelLayerRename();
      return;
    }
    const primitive = findPrimitiveById(shapePrimitives, primitiveId);
    if (!primitive) {
      cancelLayerRename();
      return;
    }
    const nextLayerName = normalizeLayerName(editingLayerName);
    const currentLayerName = normalizeLayerName(primitive.layerName);
    if (nextLayerName !== currentLayerName) {
      updatePrimitive(primitiveId, { layerName: nextLayerName });
    }
    cancelLayerRename();
  };

  const genericTextFallbackLabelById = new Map(
    shapePrimitives
      .map((shape, index) => {
        if (!isTextPrimitive(shape)) return null;
        if (normalizeTextSource(shape?.textSource, 'main') !== 'generic') return null;
        const genericIndex = shapePrimitives
          .slice(0, index + 1)
          .filter((candidate) => isTextPrimitive(candidate) && normalizeTextSource(candidate?.textSource, 'main') === 'generic')
          .length;
        return [primitiveDraftId(shape, index), `Generic text ${genericIndex}`];
      })
      .filter(Boolean),
  );

  const textLabelByPrimitiveId = new Map(
    shapePrimitives
      .map((shape, index) => {
        const primitiveId = primitiveDraftId(shape, index);
        if (!isTextPrimitive(shape)) return null;
        return [primitiveId, getPrimitiveLayerLabel(shape, index, genericTextFallbackLabelById)];
      })
      .filter(Boolean),
  );

  const baseLayerRows = layerPanelOrder
    .filter((layerId) => !TEMPLATE_BASE_LAYER_IDS.has(layerId))
    .map((layerId) => {
      const primitiveId = layerIdToPrimitiveId(layerId);
      const primitive = primitiveId
        ? shapePrimitives.find((shape, index) => primitiveDraftId(shape, index) === primitiveId)
        : null;
      const primitiveIndex = primitiveId
        ? shapePrimitives.findIndex((shape, index) => primitiveDraftId(shape, index) === primitiveId)
        : -1;
      const selectionKey = layerIdToSelectionKey(layerId);
      const isTransformable = Boolean(selectionKey);
      const isTextRow = primitive?.layerKind === 'text';
      const isImageRow = isImagePrimitive(primitive);
      let label = 'Layer';
      let defaultLabel = 'Layer';
      let kind = 'generic';
      let thumbnailType = 'text';
      let thumbnailShapeType = null;
      const customLayerName = normalizeLayerName(primitive?.layerName);
      if (primitive) {
        if (isImageRow) {
          defaultLabel = imagePrimitiveEntryById.get(primitiveId)?.defaultLabel || 'Image';
          label = customLayerName || defaultLabel;
          kind = 'image';
          thumbnailType = 'image';
        } else {
          defaultLabel = getPrimitiveLayerFallbackLabel(primitive, primitiveIndex, genericTextFallbackLabelById);
          label = customLayerName || defaultLabel;
          kind = primitive.layerKind === 'text' ? 'text' : 'primitive';
          thumbnailType = primitive.layerKind === 'text' ? 'text' : 'shape';
          thumbnailShapeType = primitive.type;
        }
      }
      const visible = isLayerVisible(layerId);
      const locked = isLayerLocked(layerId);
      const isSelected = selectionKey ? selectionKeys.includes(selectionKey) : activeLayerPanelId === layerId;
      const isFocused = activeLayerPanelId === layerId;
      const autoSizeBinding = sanitizeAutoSizeBinding(primitive?.autoSizeBinding, primitive);
      return {
        id: layerId,
        label,
        defaultLabel,
        hasCustomLabel: Boolean(customLayerName),
        kind,
        visible,
        locked,
        isSelected,
        isFocused,
        isTransformable,
        isTextRow,
        isImageRow,
        selectionKey,
        primitiveId,
        primitive,
        autoSizeBinding,
        bindingSourceTextId: autoSizeBinding?.sourceTextId || null,
        bindingSourceTextIds: autoSizeBinding ? getAutoSizeBindingSourceIds(autoSizeBinding) : [],
        thumbnailType,
        thumbnailShapeType,
      };
    });
  const baseLayerRowById = new Map(baseLayerRows.map((row) => [row.id, row]));
  const textParentRowByPrimitiveId = new Map(
    baseLayerRows
      .filter((row) => row.isTextRow && row.primitiveId)
      .map((row) => [row.primitiveId, row]),
  );
  const nestedChildRowIds = new Set();
  const childRowsByParentId = new Map();
  const linkedShapeCountByTextId = new Map();
  for (const row of baseLayerRows) {
    if (!row.isTextRow && row.primitiveId && Array.isArray(row.bindingSourceTextIds)) {
      row.bindingSourceTextIds.forEach((sourceId) => {
        if (!sourceId) return;
        linkedShapeCountByTextId.set(sourceId, (linkedShapeCountByTextId.get(sourceId) || 0) + 1);
      });
    }
    if (!row.bindingSourceTextId || !row.primitiveId || row.isTextRow) continue;
    if ((row.bindingSourceTextIds || []).length !== 1) continue;
    const parentRow = textParentRowByPrimitiveId.get(row.bindingSourceTextId);
    if (!parentRow) continue;
    nestedChildRowIds.add(row.id);
    const children = childRowsByParentId.get(parentRow.id) || [];
    children.push(row.id);
    childRowsByParentId.set(parentRow.id, children);
  }
  const layerRows = baseLayerRows
    .filter((row) => !nestedChildRowIds.has(row.id))
    .map((row) => {
      const childIds = childRowsByParentId.get(row.id) || [];
      const childRows = childIds
        .map((childId) => baseLayerRowById.get(childId))
        .filter(Boolean)
        .map((childRow) => ({
          ...childRow,
          isBindingChild: true,
          hasBrokenBinding: false,
          nestingLevel: 1,
          childRows: [],
          linkedChildCount: 0,
          isBindingParent: false,
        }));
      const hasBrokenBinding = Boolean(
        (row.bindingSourceTextIds || []).length > 0
        && !row.isTextRow
        && (row.bindingSourceTextIds || []).some((sourceId) => !textParentRowByPrimitiveId.has(sourceId)),
      );
      const bindingSourceLabels = (row.bindingSourceTextIds || [])
        .map((sourceId) => ({
          id: sourceId,
          label: textLabelByPrimitiveId.get(sourceId) || (sourceId === row.bindingSourceTextId ? 'Linked text' : sourceId),
          missing: !textParentRowByPrimitiveId.has(sourceId),
          isPrimary: sourceId === row.bindingSourceTextId,
        }));
      const linkedShapeCount = row.isTextRow
        ? (linkedShapeCountByTextId.get(row.primitiveId) || 0)
        : childRows.length;
      return {
        ...row,
        isBindingParent: row.isTextRow ? linkedShapeCount > 0 : childRows.length > 0,
        linkedChildCount: linkedShapeCount,
        hasBrokenBinding,
        isMultiSourceBinding: !row.isTextRow && (row.bindingSourceTextIds || []).length > 1,
        bindingSourceLabels,
        isBindingChild: false,
        nestingLevel: 0,
        childRows,
      };
    });

  const handleLayerRowDragStart = (layerId) => (event) => {
    if (isLayerLocked(layerId)) {
      event.preventDefault();
      return;
    }
    setDraggingLayerId(layerId);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', layerId);
    }
  };

  const handleLayerRowDragOver = (row) => (event) => {
    if (!draggingLayerId) return;
    if (draggingLayerId === row.id) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  };

  const handleLayerRowDrop = (row) => (event) => {
    if (!draggingLayerId) return;
    event.preventDefault();
    const fromIndex = layerPanelOrder.indexOf(draggingLayerId);
    const toIndex = layerPanelOrder.indexOf(row.id);
    if (fromIndex >= 0 && toIndex >= 0 && !isLayerLocked(draggingLayerId)) {
      reorderLayersByPanelIndices(fromIndex, toIndex);
    }
    setDraggingLayerId(null);
  };

  const renderLayerRow = (row) => {
    const isEditingLayer = editingLayerId === row.id;
    const RowMainElement = isEditingLayer ? 'div' : 'button';
    const rowMainProps = isEditingLayer
      ? {}
      : {
        type: 'button',
        onClick: (event) => handleLayerRowActivate(row.id, event),
        disabled: isRecording,
        'aria-pressed': row.isSelected || row.isFocused,
      };

    return (
      <React.Fragment key={row.id}>
        <div
          role="listitem"
          className={[
            'layer-row',
            row.isSelected ? 'selected' : '',
            row.isFocused ? 'focused' : '',
            row.visible ? '' : 'is-hidden',
            row.locked ? 'is-locked' : '',
            draggingLayerId === row.id ? 'is-dragging' : '',
            row.isBindingParent ? 'is-binding-parent' : '',
            row.isBindingChild ? 'is-binding-child' : '',
            row.hasBrokenBinding ? 'has-broken-binding' : '',
          ].filter(Boolean).join(' ')}
          draggable={!isRecording && !row.locked && !isEditingLayer}
          onDragStart={handleLayerRowDragStart(row.id)}
          onDragOver={handleLayerRowDragOver(row)}
          onDrop={handleLayerRowDrop(row)}
          onDragEnd={() => setDraggingLayerId(null)}
        >
          <RowMainElement
            className={`layer-row-main ${row.isBindingChild ? 'is-nested' : ''} ${isEditingLayer ? 'is-editing' : ''}`.trim()}
            {...rowMainProps}
          >
            <span className="layer-row-drag-handle" aria-hidden="true">::</span>
            <span className={`layer-row-kind ${row.kind}`} aria-hidden="true">
              {row.thumbnailType === 'shape'
                ? <PrimitiveShapeIcon type={row.thumbnailShapeType} />
                : (row.thumbnailType === 'logo' ? 'L' : 'T')}
            </span>
            {isEditingLayer ? (
              <input
                ref={editingLayerInputRef}
                type="text"
                className="layer-row-label-input"
                aria-label="Layer name"
                value={editingLayerName}
                onChange={(event) => setEditingLayerName(event.target.value)}
                onKeyDown={(event) => {
                  event.stopPropagation();
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    event.currentTarget.blur();
                    return;
                  }
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    cancelLayerRenameRef.current = true;
                    cancelLayerRename();
                  }
                }}
                onBlur={() => {
                  if (cancelLayerRenameRef.current) {
                    cancelLayerRenameRef.current = false;
                    return;
                  }
                  commitLayerRename();
                }}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
                onDoubleClick={(event) => event.stopPropagation()}
              />
            ) : (
              <span
                className={`layer-row-label ${row.hasCustomLabel ? 'is-custom' : ''}`}
                onDoubleClick={(event) => beginLayerRename(row, event)}
              >
                {row.label}
              </span>
            )}
            {row.isBindingParent && (
              <span className="layer-row-badge binding-parent">{`Linked shapes: ${row.linkedChildCount}`}</span>
            )}
            {row.isBindingChild && !row.hasBrokenBinding && (
              <span className="layer-row-badge binding-child">Auto-size</span>
            )}
            {row.hasBrokenBinding && (
              <span className="layer-row-badge binding-broken">Broken link</span>
            )}
            {row.isMultiSourceBinding && Array.isArray(row.bindingSourceLabels) && row.bindingSourceLabels.length > 0 && (
              <span className="layer-row-linked-source-list" aria-label={`Linked texts: ${row.bindingSourceLabels.map((entry) => entry.label).join(', ')}`}>
                {row.bindingSourceLabels.map((entry) => (
                  <span
                    key={`${row.id}-${entry.id}`}
                    className={`layer-row-linked-source-chip ${entry.isPrimary ? 'is-primary' : ''} ${entry.missing ? 'is-missing' : ''}`}
                    title={entry.isPrimary ? `${entry.label} (primary)` : entry.label}
                  >
                    {entry.label}
                  </span>
                ))}
              </span>
            )}
          </RowMainElement>
          <div className="layer-row-actions">
            {row.isBindingParent && row.primitiveId && (
              <button
                type="button"
                className="secondary-btn layer-row-action layer-row-action-select-linked"
                onClick={(event) => {
                  event.stopPropagation();
                  selectLinkedShapesForText(row.primitiveId);
                }}
                disabled={isRecording}
                aria-label={`Select linked shapes for ${row.label}`}
                title="Select linked"
              >
                Select linked
              </button>
            )}
            {row.isMultiSourceBinding && row.primitiveId && Array.isArray(row.bindingSourceLabels) && row.bindingSourceLabels.map((entry) => (
              <button
                key={`remove-link-${row.primitiveId}-${entry.id}`}
                type="button"
                className="secondary-btn layer-row-action layer-row-action-remove-link"
                onClick={(event) => {
                  event.stopPropagation();
                  removePrimitiveAutoSizeSourceLink(row.primitiveId, entry.id);
                }}
                disabled={isRecording || row.locked}
                aria-label={`Remove ${entry.label} auto-size link from ${row.label}`}
                title={`Remove link: ${entry.label}`}
              >
                {`Remove ${entry.label}`}
              </button>
            ))}
            {((row.isBindingChild || row.hasBrokenBinding) || (!row.isTextRow && (row.bindingSourceTextIds || []).length > 0)) && row.primitiveId && (
              <button
                type="button"
                className="secondary-btn layer-row-action layer-row-action-unbind"
                onClick={(event) => {
                  event.stopPropagation();
                  unbindPrimitiveAutoSize(row.primitiveId);
                }}
                disabled={isRecording || row.locked}
                aria-label={`Unbind auto-size for ${row.label}`}
                title={(row.isMultiSourceBinding ? 'Unbind all auto-size links' : 'Unbind auto-size')}
              >
                {row.isMultiSourceBinding ? 'Unbind all' : 'Unbind'}
              </button>
            )}
            <button type="button" className={`secondary-btn layer-row-action ${row.visible ? 'is-active' : ''}`} onClick={(event) => { event.stopPropagation(); toggleLayerVisibility(row.id); }} disabled={isRecording} aria-label={`${row.visible ? 'Hide' : 'Show'} ${row.label}`} title={row.visible ? 'Hide layer' : 'Show layer'}>{row.visible ? <LayerEyeOpenIcon /> : <LayerEyeClosedIcon />}</button>
            <button type="button" className={`secondary-btn layer-row-action ${row.locked ? 'is-active' : ''}`} onClick={(event) => { event.stopPropagation(); setLayerLock(row.id, !row.locked); }} disabled={isRecording} aria-label={`${row.locked ? 'Unlock' : 'Lock'} ${row.label}`} title={row.locked ? 'Unlock layer' : 'Lock layer'}>{row.locked ? <LayerLockClosedIcon /> : <LayerLockOpenIcon />}</button>
            {row.primitiveId && (
              <>
                {row.isImageRow && (row.isSelected || row.isFocused) && (
                  <button
                    type="button"
                    className="secondary-btn layer-row-action layer-row-action-replace"
                    onClick={(event) => {
                      event.stopPropagation();
                      openReplaceImageImport(row.primitiveId);
                    }}
                    disabled={isRecording || row.locked}
                    aria-label={`Replace image in ${row.label}`}
                    title="Replace image"
                  >
                    Replace
                  </button>
                )}
                <button type="button" className="secondary-btn layer-row-action layer-row-action-icon-btn layer-row-action-duplicate" onClick={(event) => { event.stopPropagation(); duplicatePrimitiveById(row.primitiveId); }} disabled={isRecording || row.locked} aria-label={`Duplicate ${row.label}`} title="Duplicate"><LayerDuplicateIcon /></button>
                <button type="button" className="secondary-btn preset-delete-btn layer-row-action layer-row-action-icon-btn layer-row-action-delete" onClick={(event) => { event.stopPropagation(); deletePrimitiveById(row.primitiveId); }} disabled={isRecording || row.locked} aria-label={`Delete ${row.label}`} title="Delete"><LayerDeleteIcon /></button>
              </>
            )}
          </div>
        </div>
        {row.childRows?.length > 0 && (
          <div className="layer-row-children" role="group" aria-label={`Layers linked to ${row.label}`}>
            {row.childRows.map((childRow) => renderLayerRow(childRow))}
          </div>
        )}
      </React.Fragment>
    );
  };

  const selectionControlsPanel = (
    <div className="preset-overlay-main-controls">
      <div className="preset-overlay-controls-header">
        <h3>Transform selected</h3>
      </div>
      {selectedPrimitive && (
        <>
          {selectedPrimitive.type === 'parallelogram' && !selectedPrimitiveIsTextLayer && !selectedPrimitiveIsImageLayer && (
            <div className={`primitive-subpanel ${selectedPrimitiveControlsDisabled ? 'is-disabled' : ''}`}>
              <div className="primitive-subpanel-header">
                <h4 className="primitive-subpanel-title">Geometry</h4>
              </div>
              <div className="primitive-subpanel-body">
                <div className="preset-position-inputs primitive-inputs">
                  <label>
                    Skew X
                    <input
                      className="compact-number-input"
                      type="number"
                      step="1"
                      value={selectedPrimitive.skewX || 0}
                      onChange={handleSelectedPrimitiveNumberChange('skewX')}
                      disabled={selectedPrimitiveControlsDisabled}
                    />
                  </label>
                </div>
              </div>
            </div>
          )}
          {selectedPrimitiveIsImageLayer && (
            <div className={`primitive-subpanel ${selectedPrimitiveControlsDisabled ? 'is-disabled' : ''}`}>
              <div className="primitive-subpanel-header">
                <h4 className="primitive-subpanel-title">Image</h4>
              </div>
              <div className="primitive-subpanel-body">
                <div className="primitive-action-row">
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => openReplaceImageImport(selectedPrimitiveId)}
                    disabled={selectedPrimitiveControlsDisabled}
                  >
                    Replace image
                  </button>
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => deletePrimitiveById(selectedPrimitiveId)}
                    disabled={selectedPrimitiveControlsDisabled}
                    style={{ color: '#f87171' }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          )}

          {selectedPrimitiveIsTextLayer && (
            <>
              {selectedPrimitiveIsGenericTextLayer && (
                <div className={`primitive-subpanel ${selectedPrimitiveControlsDisabled ? 'is-disabled' : ''}`}>
                  <div className="primitive-subpanel-header">
                    <h4 className="primitive-subpanel-title">Content</h4>
                  </div>
                  <div className="primitive-subpanel-body">
                    <div className="primitive-generic-content-row">
                      <label className="primitive-generic-content-field">
                        <span className="font-picker-field-label">Preset text</span>
                        <input
                          className="font-picker-search-input generic-text-content-input"
                          type="text"
                          aria-label="Preset text"
                          value={typeof selectedPrimitive.textValue === 'string' ? selectedPrimitive.textValue : ''}
                          onChange={(e) => updatePrimitive(selectedPrimitiveId, { textValue: e.target.value })}
                          disabled={selectedPrimitiveControlsDisabled}
                        />
                      </label>
                      <div className="primitive-generic-hide-toggle">
                        <div className="primitive-generic-hide-header">
                          <span className="primitive-generic-hide-label font-picker-field-label">Dynamic</span>
                          <span className="primitive-generic-info-wrap">
                            <button
                              type="button"
                              className="primitive-generic-info-trigger"
                              aria-label={DYNAMIC_TEXT_TOOLTIP}
                              tabIndex={selectedPrimitiveControlsDisabled ? -1 : 0}
                              disabled={selectedPrimitiveControlsDisabled}
                            >
                              <InfoCircleIcon className="primitive-generic-info-icon" aria-hidden="true" focusable="false" />
                            </button>
                            <span role="tooltip" className="primitive-generic-info-tooltip">
                              {DYNAMIC_TEXT_TOOLTIP}
                            </span>
                          </span>
                        </div>
                        <label className="primitive-generic-checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedPrimitive.hideFromPeoplePanel !== true}
                          onChange={(e) => updatePrimitive(selectedPrimitiveId, { hideFromPeoplePanel: !e.target.checked })}
                          disabled={selectedPrimitiveControlsDisabled}
                          aria-label="Dynamic"
                        />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className={`primitive-subpanel ${selectedPrimitiveControlsDisabled ? 'is-disabled' : ''}`}>
                <div className="primitive-subpanel-header">
                  <h4 className="primitive-subpanel-title">Font</h4>
                </div>
                <div className="primitive-subpanel-body">
                  <div className="font-picker-field" ref={textFontPickerRef}>
                    <span className="font-picker-field-label">Font</span>
                    <button
                      ref={textFontPickerButtonRef}
                      type="button"
                      className={`font-picker-trigger ${isTextFontPickerOpen ? 'is-open' : ''}`}
                      onClick={toggleTextFontPicker}
                      disabled={selectedPrimitiveControlsDisabled}
                      aria-label="Text font family"
                      aria-haspopup="listbox"
                      aria-expanded={isTextFontPickerOpen}
                      aria-controls={textFontPickerDropdownId}
                    >
                      <span className="font-picker-trigger-value" style={selectedTextFontPickerPreviewStyle}>
                        {selectedTextFontPickerLabel}
                      </span>
                      <span className="font-picker-trigger-caret" aria-hidden="true">▾</span>
                    </button>
                    {isTextFontPickerOpen && (
                      <div className="font-picker-dropdown">
                        <label className="font-picker-search-field">
                          <span className="font-picker-search-label">Search</span>
                          <input
                            ref={textFontPickerSearchInputRef}
                            className="font-picker-search-input"
                            type="search"
                            value={textFontSearchQuery}
                            onChange={(e) => setTextFontSearchQuery(e.target.value)}
                            aria-label="Search text fonts"
                            placeholder="Search Google Fonts"
                          />
                        </label>
                        <div
                          id={textFontPickerDropdownId}
                          className="font-picker-options"
                          role="listbox"
                          aria-label="Text font options"
                        >
                          <button
                            type="button"
                            className={`font-picker-option ${selectedPrimitiveTextFontFamily == null ? 'selected' : ''}`}
                            onClick={() => selectTextFontFamily(null)}
                            role="option"
                            aria-selected={selectedPrimitiveTextFontFamily == null}
                          >
                            <span className="font-picker-option-name">{TEXT_FONT_DEFAULT_OPTION_LABEL}</span>
                            <span className="font-picker-option-check" aria-hidden="true">
                              {selectedPrimitiveTextFontFamily == null ? '✓' : ''}
                            </span>
                          </button>
                          {visibleTextLayerFontFamilies.map((family) => {
                            const isSelected = selectedPrimitiveTextFontFamily === family;
                            const previewStyle = loadedTextFontPreviewFamilySet.has(family)
                              ? { fontFamily: buildFontPreviewFamily(family) }
                              : undefined;
                            return (
                              <button
                                key={family}
                                type="button"
                                className={`font-picker-option ${isSelected ? 'selected' : ''}`}
                                onClick={() => selectTextFontFamily(family)}
                                role="option"
                                aria-selected={isSelected}
                              >
                                <span className="font-picker-option-name" style={previewStyle}>{family}</span>
                                <span className="font-picker-option-check" aria-hidden="true">
                                  {isSelected ? '✓' : ''}
                                </span>
                              </button>
                            );
                          })}
                          {!visibleTextLayerFontFamilies.length && (
                            <div className="font-picker-empty-state">No fonts found</div>
                          )}
                          {textFontSearchQuery.trim() && textFontSearchHasMoreResults && (
                            <div className="font-picker-results-note">
                              {`Showing first ${TEXT_LAYER_FONT_SEARCH_RESULT_LIMIT} matches of ${textFontSearchTotalMatches}`}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="preset-position-inputs primitive-inputs">
                    <label>
                      Text padding X
                      <input
                        className="compact-number-input"
                        type="number"
                        step="1"
                        value={selectedPrimitive.textPaddingX ?? 0}
                        onChange={handleSelectedPrimitiveNumberChange('textPaddingX')}
                        disabled={selectedPrimitiveControlsDisabled}
                      />
                    </label>
                    <label>
                      Font ratio
                      <input
                        className="compact-number-input"
                        type="number"
                        step="0.01"
                        value={selectedPrimitive.textFontRatio ?? 0.8}
                        onChange={handleSelectedPrimitiveNumberChange('textFontRatio')}
                        disabled={selectedPrimitiveControlsDisabled}
                      />
                    </label>
                    <label>
                      Baseline ratio
                      <input
                        className="compact-number-input"
                        type="number"
                        step="0.01"
                        value={selectedPrimitive.textBaselineRatio ?? 0.78}
                        onChange={handleSelectedPrimitiveNumberChange('textBaselineRatio')}
                        disabled={selectedPrimitiveControlsDisabled}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className={`primitive-subpanel ${selectedPrimitiveControlsDisabled ? 'is-disabled' : ''}`}>
                <div className="primitive-subpanel-header">
                  <h4 className="primitive-subpanel-title">Text color</h4>
                </div>
                <div className="primitive-subpanel-body">
                  <div className="primitive-color-row">
                    <label>
                      Text color source
                      <select
                        aria-label="Text color source"
                        value={selectedPrimitiveColorDraft.fillMode}
                        onChange={(e) => updatePrimitive(selectedPrimitiveId, { fillColorMode: e.target.value })}
                        disabled={selectedPrimitiveControlsDisabled}
                      >
                        {PRIMITIVE_COLOR_MODES.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                    {selectedPrimitiveColorDraft.fillMode === 'theme' ? (
                      <label>
                        Text preset color
                        <select
                          aria-label="Text preset color"
                          value={selectedPrimitiveColorDraft.fillRole}
                          onChange={(e) => updatePrimitive(selectedPrimitiveId, {
                            colorRole: e.target.value,
                            fillColorRole: e.target.value,
                            strokeColorRole: e.target.value,
                          })}
                          disabled={selectedPrimitiveControlsDisabled}
                        >
                          {PRIMITIVE_COLOR_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </label>
                    ) : (
                      <label>
                        Text color
                        <input
                          aria-label="Text color"
                          type="color"
                          value={selectedPrimitiveColorDraft.fillValue}
                          onChange={(e) => updatePrimitive(selectedPrimitiveId, {
                            fillColorValue: e.target.value,
                            strokeColorValue: e.target.value,
                          })}
                          disabled={selectedPrimitiveControlsDisabled}
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {!selectedPrimitiveIsTextLayer && !selectedPrimitiveIsImageLayer && selectedPrimitiveCanFill && (
            <div className={`primitive-subpanel ${selectedPrimitiveControlsDisabled ? 'is-disabled' : ''}`}>
              <div className="primitive-subpanel-header">
                <h4 className="primitive-subpanel-title">Fill</h4>
                <label className="logo-toggle primitive-subpanel-toggle">
                  <input
                    type="checkbox"
                    checked={selectedPrimitive.fill !== false}
                    onChange={(e) => updatePrimitive(selectedPrimitiveId, { fill: e.target.checked })}
                    disabled={selectedPrimitiveControlsDisabled}
                    aria-label="Enable fill"
                  />
                  On
                </label>
              </div>
              <div className="primitive-subpanel-body">
                <div className="primitive-color-row primitive-compact-controls-row primitive-fill-controls-row">
                  {selectedPrimitive.type === 'roundedRect' && (
                    <label>
                      Corner radius
                      <input
                        className="compact-number-input"
                        type="number"
                        step="1"
                        value={selectedPrimitive.cornerRadius}
                        onChange={handleSelectedPrimitiveNumberChange('cornerRadius')}
                        disabled={selectedPrimitiveControlsDisabled}
                      />
                    </label>
                  )}
                  <label className={`primitive-color-control ${fillControlsDisabled ? 'is-disabled' : ''}`}>
                    Fill source
                    <select
                      aria-label="Fill source"
                      value={selectedPrimitiveColorDraft.fillMode}
                      onChange={(e) => updatePrimitive(selectedPrimitiveId, { fillColorMode: e.target.value })}
                      disabled={selectedPrimitiveControlsDisabled || fillControlsDisabled}
                    >
                      {PRIMITIVE_COLOR_MODES.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  {selectedPrimitiveColorDraft.fillMode === 'theme' ? (
                    <label className={`primitive-color-control ${fillControlsDisabled ? 'is-disabled' : ''}`}>
                      Fill preset color
                      <select
                        aria-label="Fill preset color"
                        value={selectedPrimitiveColorDraft.fillRole}
                        onChange={(e) => updatePrimitive(selectedPrimitiveId, { fillColorRole: e.target.value })}
                        disabled={selectedPrimitiveControlsDisabled || fillControlsDisabled}
                      >
                        {PRIMITIVE_COLOR_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                  ) : (
                    <label className={`primitive-color-control ${fillControlsDisabled ? 'is-disabled' : ''}`}>
                      Fill color
                      <input
                        aria-label="Fill color"
                        type="color"
                        value={selectedPrimitiveColorDraft.fillValue}
                        onChange={(e) => updatePrimitive(selectedPrimitiveId, { fillColorValue: e.target.value })}
                        disabled={selectedPrimitiveControlsDisabled || fillControlsDisabled}
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>
          )}

          {!selectedPrimitiveIsTextLayer && !selectedPrimitiveIsImageLayer && selectedPrimitiveShowsStrokeSection && (
            <div className={`primitive-subpanel ${selectedPrimitiveControlsDisabled || strokeControlsDisabled ? 'is-disabled' : ''}`}>
              <div className="primitive-subpanel-header">
                <h4 className="primitive-subpanel-title">Stroke</h4>
                <label className="logo-toggle primitive-subpanel-toggle">
                  <input
                    type="checkbox"
                    checked={selectedPrimitive.type === 'line' ? true : Boolean(selectedPrimitive.stroke)}
                    onChange={(e) => updatePrimitive(selectedPrimitiveId, { stroke: e.target.checked })}
                    disabled={selectedPrimitiveControlsDisabled || selectedPrimitive.type === 'line'}
                    aria-label="Enable stroke"
                  />
                  On
                </label>
              </div>
              <div className="primitive-subpanel-body">
                <div className="primitive-color-row primitive-compact-controls-row primitive-stroke-controls-row">
                  <label>
                    Thickness
                    <input
                      className="compact-number-input"
                      type="number"
                      step="1"
                      value={selectedPrimitive.strokeWidth}
                      onChange={handleSelectedPrimitiveNumberChange('strokeWidth')}
                      disabled={selectedPrimitiveControlsDisabled || strokeControlsDisabled}
                    />
                  </label>
                  <label>
                    Stroke source
                    <select
                      aria-label="Stroke source"
                      value={selectedPrimitiveColorDraft.strokeMode}
                      onChange={(e) => updatePrimitive(selectedPrimitiveId, { strokeColorMode: e.target.value })}
                      disabled={selectedPrimitiveControlsDisabled || strokeControlsDisabled}
                    >
                      {PRIMITIVE_COLOR_MODES.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  {selectedPrimitiveColorDraft.strokeMode === 'theme' ? (
                    <label>
                      Stroke preset color
                      <select
                        aria-label="Stroke preset color"
                        value={selectedPrimitiveColorDraft.strokeRole}
                        onChange={(e) => updatePrimitive(selectedPrimitiveId, { strokeColorRole: e.target.value })}
                        disabled={selectedPrimitiveControlsDisabled || strokeControlsDisabled}
                      >
                        {PRIMITIVE_COLOR_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                  ) : (
                    <label>
                      Stroke color
                      <input
                        aria-label="Stroke color"
                        type="color"
                        value={selectedPrimitiveColorDraft.strokeValue}
                        onChange={(e) => updatePrimitive(selectedPrimitiveId, { strokeColorValue: e.target.value })}
                        disabled={selectedPrimitiveControlsDisabled || strokeControlsDisabled}
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <>
      <div
        className="preset-overlay-backdrop"
        role="dialog"
        aria-modal="true"
        aria-label="Edit presets"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        <div className="preset-overlay-modal">
          <div className="preset-overlay-left">
            <div className="preset-overlay-left-header">
              <h3>Presets</h3>
              {isDraftDirty && <span className="preset-dirty-dot">Unsaved changes</span>}
            </div>
            <div className="preset-list">
              {presets.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  className={`preset-list-item ${preset.id === selectedPresetId ? 'selected' : ''}`}
                  onClick={() => onSelectPreset(preset.id)}
                  disabled={isRecording}
                >
                  <span>{preset.name}</span>
                  <small>{preset.isBuiltIn ? 'Built-in' : 'Custom'}</small>
                </button>
              ))}
            </div>
            <button type="button" className="secondary-btn" onClick={onCreateNew} disabled={isRecording}>
              Create New
            </button>
          </div>

          <div className="preset-overlay-main">
            <div className="preset-overlay-main-header">
              <h3>Edit preset</h3>
              <div className="preset-main-actions">
                {!draftPreset.isBuiltIn && (
                  <button type="button" className="secondary-btn preset-delete-btn" onClick={onDeletePreset} disabled={isRecording}>
                    Delete
                  </button>
                )}
                <button type="button" className="secondary-btn preset-save-btn" onClick={onSavePreset} disabled={isRecording}>
                  {draftPreset.isBuiltIn ? 'Save as new' : 'Save preset'}
                </button>
                <button type="button" className="secondary-btn" onClick={onClose}>Discard</button>
              </div>
            </div>

            <div className="preset-overlay-grid">
              <div className="control-group">
                <label>Name</label>
                {draftPreset.isBuiltIn ? (
                  <div className="preset-readonly-name">{draftPreset.name}</div>
                ) : (
                  <input type="text" value={draftPreset.name} onChange={handleField('name')} disabled={isRecording} />
                )}
              </div>
              <div className="control-group">
                <label>Shape family</label>
                <select value={draftPreset.preset} onChange={handlePresetFamilyChange} disabled={isRecording}>
                  {PRESET_STYLE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="overlay-preview-block static-editor-preview">
              <div className="overlay-preview-header">
                <h4>Static Preview (Click/Shift-click to select, drag empty space to box-select)</h4>
                <div className="overlay-preview-header-actions">
                  <span>X {Math.round(draftPreset.posX)} | Y {Math.round(draftPreset.posY)}</span>
                </div>
              </div>

              <div className={`static-preview-stage ${isPortraitPreview ? 'is-portrait' : ''}`} style={staticPreviewStageStyle}>
                <div className="preset-preview-toolbar">
                  <button
                    type="button"
                    className="secondary-btn preset-preview-undo-btn"
                    onClick={() => onUndoDraft?.()}
                    disabled={isRecording || !canUndoDraft}
                    aria-label="Undo last preset edit"
                    title="Undo (Ctrl/Cmd+Z)"
                  >
                    Undo
                  </button>
                  <div className="preset-preview-zoom-controls">
                    <button
                      type="button"
                      className="secondary-btn preset-preview-zoom-btn"
                      onClick={handleZoomOut}
                      disabled={isRecording || !canZoomOut}
                      aria-label="Zoom out preview"
                      title="Zoom out preview"
                    >
                      <MinusIcon />
                    </button>
                    <button
                      type="button"
                      className="secondary-btn preset-preview-zoom-btn"
                      onClick={handleZoomIn}
                      disabled={isRecording || !canZoomIn}
                      aria-label="Zoom in preview"
                      title="Zoom in preview"
                    >
                      <PlusIcon />
                    </button>
                    <button
                      type="button"
                      className="secondary-btn preset-preview-reset-btn"
                      onClick={handleResetZoom}
                      disabled={isRecording || previewZoomPercent === PRESET_PREVIEW_ZOOM_DEFAULT}
                      aria-label="Reset preview zoom"
                      title="Reset preview zoom"
                    >
                      Reset zoom
                    </button>
                  </div>
                </div>
                <div className="preset-preview-snap-controls snap-controls-row">
                  {isPresetGridSnapActive && (
                    <>
                      <select
                        className="snap-grid-type-select preset-preview-snap-grid-type-select"
                        value={snapGridType}
                        onChange={(event) => setSnapGridType(event.target.value)}
                        disabled={isRecording}
                        aria-label="Preview snap grid type"
                        title="Preview snap grid type"
                      >
                        {SNAP_GRID_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className={`secondary-btn snap-toggle-btn snap-grid-visibility-btn ${showSnapGrid ? 'is-active' : ''}`}
                        onClick={() => setIsSnapGridVisible((prev) => !prev)}
                        disabled={isRecording}
                        aria-pressed={showSnapGrid}
                        aria-label={isSnapGridVisible ? 'Hide preview snap grid' : 'Show preview snap grid'}
                        title={isSnapGridVisible ? 'Hide preview snap grid' : 'Show preview snap grid'}
                      >
                        {isSnapGridVisible ? <LayerEyeOpenIcon /> : <LayerEyeClosedIcon />}
                      </button>
                    </>
                  )}
                  <div className="snap-mode-menu" ref={presetSnapModeMenuRef}>
                    <div className="snap-mode-split">
                      <button
                        type="button"
                        className={`secondary-btn snap-toggle-btn preset-preview-snap-toggle snap-mode-main-btn ${isSnapEnabled ? 'is-active' : ''}`}
                        onClick={() => setIsSnapEnabled((prev) => !prev)}
                        disabled={isRecording}
                        aria-pressed={isSnapEnabled}
                        aria-label={isSnapEnabled ? 'Disable preview snapping' : 'Enable preview snapping'}
                        title={isSnapEnabled ? 'Snapping on' : 'Snapping off'}
                      >
                        <svg viewBox="0 0 16 16" aria-hidden="true">
                          <path d="M1 6V9C1 12.866 4.13401 16 8 16C11.866 16 15 12.866 15 9V6H10V9C10 10.1046 9.10457 11 8 11C6.89543 11 6 10.1046 6 9V6H1Z" fill="currentColor" />
                          <path d="M1 4H6V1H1V4Z" fill="currentColor" />
                          <path d="M10 4H15V1H10V4Z" fill="currentColor" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className={`secondary-btn snap-mode-toggle-btn ${isPresetSnapModeMenuOpen ? 'is-open' : ''}`}
                        onClick={() => setIsPresetSnapModeMenuOpen((prev) => !prev)}
                        disabled={isRecording}
                        aria-label="Preview snap mode options"
                        aria-haspopup="menu"
                        aria-expanded={isPresetSnapModeMenuOpen}
                        title="Preview snap mode options"
                      >
                        <span className="snap-mode-toggle-caret" aria-hidden="true">▾</span>
                      </button>
                    </div>
                    {isPresetSnapModeMenuOpen && (
                      <div className="snap-mode-dropdown" role="menu" aria-label="Preview snap mode options">
                        {PRESET_SNAP_MODE_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            role="menuitemradio"
                            aria-checked={presetSnapMode === option.value}
                            className={`snap-mode-option ${presetSnapMode === option.value ? 'selected' : ''}`}
                            onClick={() => {
                              setPresetSnapMode(option.value);
                              setIsSnapEnabled(true);
                              setIsPresetSnapModeMenuOpen(false);
                            }}
                            disabled={isRecording}
                            title={option.label}
                          >
                            <span className="snap-mode-option-label">{option.label}</span>
                            <span className="snap-mode-option-check" aria-hidden="true">
                              {presetSnapMode === option.value ? '✓' : ''}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="static-preview-stage-viewport themed-scrollbar">
                  <div className="static-preview-stage-content" style={staticPreviewContentStyle}>
                    <div className="static-preview-stage-frame" style={staticPreviewFrameStyle}>
                      {showSnapGrid && (
                        <div className="snap-grid-overlay preset-snap-grid-overlay" aria-hidden="true">
                          {visibleSnapGrid.x.map((x) => (
                            <div
                              key={`pgrid-vx-${x}`}
                              className="snap-grid-line vertical"
                              style={{ left: `${(x / canvasWidth) * 100}%` }}
                            />
                          ))}
                          {visibleSnapGrid.y.map((y) => (
                            <div
                              key={`pgrid-hy-${y}`}
                              className="snap-grid-line horizontal"
                              style={{ top: `${(y / canvasHeight) * 100}%` }}
                            />
                          ))}
                        </div>
                      )}
                      {(snapGuides.vertical.length > 0 || snapGuides.horizontal.length > 0) && (
                        <div className="position-snap-guides preset-snap-guides" aria-hidden="true">
                          {snapGuides.vertical.map((x) => (
                            <div
                              key={`pvx-${x}`}
                              className="position-snap-guide vertical"
                              style={{ left: `${(x / canvasWidth) * 100}%` }}
                            />
                          ))}
                          {snapGuides.horizontal.map((y) => (
                            <div
                              key={`phy-${y}`}
                              className="position-snap-guide horizontal"
                              style={{ top: `${(y / canvasHeight) * 100}%` }}
                            />
                          ))}
                        </div>
                      )}
                      <canvas
                        ref={previewCanvasRef}
                        className={`preset-overlay-preview-canvas preview-canvas preview-canvas-alpha static-preview-canvas ${canvasCursorClass}`}
                        style={{ aspectRatio: canvasAspectRatio }}
                        onMouseDown={handleCanvasMouseDown}
                        onMouseMove={handleCanvasMouseMove}
                        onMouseLeave={handleCanvasMouseLeave}
                      />
                      {bindingDropPreviewBoxStyle && (
                        <div className={`binding-drop-preview-box ${bindingDropPreview?.action === 'rebind' || bindingDropPreview?.action === 'append' ? 'is-rebind' : ''}`} style={bindingDropPreviewBoxStyle} />
                      )}
                      {bindingDropPreviewChipStyle && bindingDropPreview?.label && (
                        <div className={`binding-drop-preview-chip ${bindingDropPreview?.action === 'rebind' || bindingDropPreview?.action === 'append' ? 'is-rebind' : ''}`} style={bindingDropPreviewChipStyle}>
                          {bindingDropPreview.label}
                        </div>
                      )}
                      {marqueeBoundsStyle && <div className="selection-marquee-box" style={marqueeBoundsStyle} />}
                      {selectedBoundsStyle && (
                        <div className="selection-transform-box" style={selectedBoundsStyle}>
                          <button type="button" className="selection-box-handle nw" aria-label="Scale selection from top left" onMouseDown={startHandleDrag({ mode: 'scale', scaleMode: 'uniform', handleId: 'nw' })} />
                          <button type="button" className="selection-box-handle ne" aria-label="Scale selection from top right" onMouseDown={startHandleDrag({ mode: 'scale', scaleMode: 'uniform', handleId: 'ne' })} />
                          <button type="button" className="selection-box-handle sw" aria-label="Scale selection from bottom left" onMouseDown={startHandleDrag({ mode: 'scale', scaleMode: 'uniform', handleId: 'sw' })} />
                          <button type="button" className="selection-box-handle se" aria-label="Scale selection from bottom right" onMouseDown={startHandleDrag({ mode: 'scale', scaleMode: 'uniform', handleId: 'se' })} />
                          {hasPrimitiveOnlySelection && !selectedPrimitiveIsTextLayer && (
                            <>
                              <button type="button" className="selection-box-handle n" aria-label="Scale selection height from top" onMouseDown={startHandleDrag({ mode: 'scale', scaleMode: 'y', handleId: 'n' })} />
                              <button type="button" className="selection-box-handle e" aria-label="Scale selection width from right" onMouseDown={startHandleDrag({ mode: 'scale', scaleMode: 'x', handleId: 'e' })} />
                              <button type="button" className="selection-box-handle s" aria-label="Scale selection height from bottom" onMouseDown={startHandleDrag({ mode: 'scale', scaleMode: 'y', handleId: 's' })} />
                              <button type="button" className="selection-box-handle w" aria-label="Scale selection width from left" onMouseDown={startHandleDrag({ mode: 'scale', scaleMode: 'x', handleId: 'w' })} />
                            </>
                          )}
                          <div className="selection-rotate-stem" />
                          <button type="button" className="selection-rotate-handle" aria-label="Rotate selection" onMouseDown={startHandleDrag({ mode: 'rotate' })} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {(selectedPrimitive || hasActiveSelection) && (
                <div
                  className={`position-readout position-readout-controls overlay-selected-readout ${selectedPrimitive ? '' : 'reset-only'}`.trim()}
                  role={selectedPrimitive ? 'group' : undefined}
                  aria-label={selectedPrimitive ? 'Selected object bounds' : undefined}
                >
                  {selectedPrimitive && (
                    <div className="overlay-selected-readout-fields">
                      <div className="position-readout-field">
                        <span className="position-readout-label">X</span>
                        <input
                          className="position-readout-number compact-number-input"
                          type="number"
                          step="1"
                          value={selectedPrimitive.x}
                          onChange={handleSelectedPrimitiveNumberChange('x')}
                          disabled={selectedPrimitiveControlsDisabled}
                          aria-label="Selected X position"
                        />
                      </div>
                      <div className="position-readout-field">
                        <span className="position-readout-label">Y</span>
                        <input
                          className="position-readout-number compact-number-input"
                          type="number"
                          step="1"
                          value={selectedPrimitive.y}
                          onChange={handleSelectedPrimitiveNumberChange('y')}
                          disabled={selectedPrimitiveControlsDisabled}
                          aria-label="Selected Y position"
                        />
                      </div>
                      <div className="position-readout-field">
                        <span className="position-readout-label">W</span>
                        <input
                          className="position-readout-number compact-number-input"
                          type="number"
                          step="1"
                          value={selectedPrimitive.width}
                          onChange={handleSelectedPrimitiveNumberChange('width')}
                          disabled={selectedPrimitiveControlsDisabled}
                          aria-label="Selected width"
                        />
                      </div>
                      <div className="position-readout-field">
                        <span className="position-readout-label">H</span>
                        <input
                          className="position-readout-number compact-number-input"
                          type="number"
                          step="1"
                          value={selectedPrimitive.height}
                          onChange={handleSelectedPrimitiveNumberChange('height')}
                          disabled={selectedPrimitiveControlsDisabled}
                          aria-label="Selected height"
                        />
                      </div>
                      <div className="position-readout-field">
                        <span className="position-readout-label">R</span>
                        <input
                          className="position-readout-number compact-number-input"
                          type="number"
                          step="1"
                          value={selectedPrimitive.rotation}
                          onChange={handleSelectedPrimitiveNumberChange('rotation')}
                          disabled={selectedPrimitiveControlsDisabled}
                          aria-label="Selected rotation"
                        />
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    className="secondary-btn overlay-selected-reset-btn"
                    onClick={handleResetTransforms}
                    disabled={isRecording || !hasActiveSelection || hasLockedSelection}
                  >
                    Reset transform
                  </button>
                </div>
              )}
              <div className="overlay-preview-hint">Click to select, Shift-click to toggle, drag empty space to box-select, hold Shift while dragging to constrain movement, or drag a text layer onto a shape to bind auto-size.</div>
            </div>

          </div>

          <div className="preset-overlay-right themed-scrollbar">
            <div className="preset-overlay-right-split">
              <div className="preset-overlay-right-top">
                <div className="preset-overlay-right-section-body themed-scrollbar">
                  {selectionControlsPanel}
                </div>
              </div>

              <div className="preset-overlay-right-section preset-overlay-right-middle">
                <div className="preset-overlay-right-section-header">
                  <h3>Layers</h3>
                  <small className="muted-text">Top = Front</small>
                </div>
                <div className="preset-overlay-right-section-body">
                  <div className="layer-editor-list themed-scrollbar" role="list" aria-label="Layer editor">
                    {layerRows.map((row) => renderLayerRow(row))}
                  </div>
                </div>
              </div>

              <div className="preset-overlay-right-section preset-overlay-right-bottom">
                <div className="preset-overlay-right-section-body">
                  <div className="preview-primitives-toolbar">
                    <div className="primitive-add-row">
                      <div className="primitive-add-menu" ref={primitiveAddMenuRef}>
                        <div className="primitive-add-split">
                          <button
                            type="button"
                            className="secondary-btn primitive-add-main-btn"
                            onClick={addSelectedPrimitiveType}
                            disabled={isRecording}
                            aria-label={`Add ${selectedPrimitiveAddOption.ariaLabel} shape`}
                            title={`Add ${selectedPrimitiveAddOption.ariaLabel} shape`}
                          >
                            <PrimitiveShapeIcon type={selectedPrimitiveAddOption.value} />
                            <svg className="primitive-add-plus-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <path d="M4 12H20M12 4V20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className={`secondary-btn primitive-add-toggle-btn ${isPrimitiveAddMenuOpen ? 'is-open' : ''}`}
                            onClick={() => {
                              setIsTextAddMenuOpen(false);
                              setIsPrimitiveAddMenuOpen((prev) => !prev);
                            }}
                            disabled={isRecording}
                            aria-label="Choose primitive shape"
                            aria-haspopup="menu"
                            aria-expanded={isPrimitiveAddMenuOpen}
                            title="Choose primitive shape"
                          >
                            <svg viewBox="0 0 16 16" aria-hidden="true">
                              <path d="M3 10L8 5L13 10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                        </div>
                        {isPrimitiveAddMenuOpen && (
                          <div className="primitive-add-dropdown" role="menu" aria-label="Primitive shape options">
                            {PRIMITIVE_ADD_OPTIONS.map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                className={`primitive-add-option ${option.value === selectedPrimitiveAddOption.value ? 'selected' : ''}`}
                                onClick={() => selectPrimitiveAddType(option.value)}
                                disabled={isRecording}
                                aria-label={`${option.ariaLabel} shape`}
                              >
                                <PrimitiveShapeIcon type={option.value} />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="primitive-add-menu" ref={textAddMenuRef}>
                        <div className="primitive-add-split">
                          <button
                            type="button"
                            className="secondary-btn primitive-text-main-btn"
                            onClick={addSelectedTextType}
                            disabled={isRecording}
                            aria-label={`Add ${selectedTextAddOption.ariaLabel}`}
                            title={`Add ${selectedTextAddOption.ariaLabel}`}
                          >
                            <span className="primitive-text-label">{selectedTextAddOption.label}</span>
                            <svg className="primitive-add-plus-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <path d="M4 12H20M12 4V20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className={`secondary-btn primitive-add-toggle-btn ${isTextAddMenuOpen ? 'is-open' : ''}`}
                            onClick={() => {
                              setIsPrimitiveAddMenuOpen(false);
                              setIsTextAddMenuOpen((prev) => !prev);
                            }}
                            disabled={isRecording}
                            aria-label="Choose text layer type"
                            aria-haspopup="menu"
                            aria-expanded={isTextAddMenuOpen}
                            title="Choose text layer type"
                          >
                            <svg viewBox="0 0 16 16" aria-hidden="true">
                              <path d="M3 10L8 5L13 10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                        </div>
                        {isTextAddMenuOpen && (
                          <div className="primitive-add-dropdown text-add-dropdown" role="menu" aria-label="Text layer options">
                            {TEXT_ADD_OPTIONS.map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                className={`primitive-add-option text-add-option ${option.value === selectedTextAddOption.value ? 'selected' : ''}`}
                                onClick={() => selectTextAddType(option.value)}
                                disabled={isRecording}
                                aria-label={option.ariaLabel}
                              >
                                <span className="text-add-option-label">{option.label}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="secondary-btn primitive-image-btn"
                        onClick={() => {
                          setReplacingImagePrimitiveId(null);
                          setIsImageImportOpen(true);
                        }}
                        disabled={isRecording}
                        aria-label="Add image layer"
                        title="Add image layer"
                      >
                        <AddImageLayerIcon />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ImageImportOverlay
        isOpen={isImageImportOpen}
        lockedMode={replacingImageLockedMode}
        onConfirm={({ imageData, imageCropMode, outputWidth, outputHeight }) => {
          setIsImageImportOpen(false);
          const replaceId = replacingImagePrimitiveId;
          setReplacingImagePrimitiveId(null);

          if (replaceId) {
            // Replace existing image source while preserving placement, sizing, rotation, and crop mode.
            const existing = shapePrimitives.find((s, i) => primitiveDraftId(s, i) === replaceId);
            if (existing) {
              updatePrimitive(replaceId, buildImageReplacementPatch(existing, {
                imageData,
                imageCropMode,
              }));
              return;
            }
          }

          // Add new image
          const nextShape = createImagePrimitiveDraft(
            imageData,
            imageCropMode,
            outputWidth,
            outputHeight,
            geometry?.renderBounds || null,
            canvasWidth,
            canvasHeight,
          );
          const nextShapePrimitives = [...shapePrimitives, nextShape];
          const nextLayerOrder = [...normalizePresetLayerOrder(draftPreset.layerOrder, shapePrimitives), primitiveLayerId(nextShape.id)];
          onDraftChange({
            shapePrimitives: nextShapePrimitives,
            layerOrder: normalizePresetLayerOrder(nextLayerOrder, nextShapePrimitives),
            layerLocks: normalizePresetLayerLocks(draftPreset.layerLocks, nextShapePrimitives),
          });
          setActiveLayerPanelId(primitiveLayerId(nextShape.id));
          setSelectionKeys([primitiveSelectionKey(nextShape.id)]);
        }}
        onCancel={() => {
          setIsImageImportOpen(false);
          setReplacingImagePrimitiveId(null);
        }}
      />
    </>
  );
};

export default PresetOverlay;
